import { Customer360SectionKey } from '../../../registry/customer360Sections';
import { CardDataStateKind, IntelligenceFilterId, IntelligenceSectionKey } from '../../../types';
import { CustomerInsightsKey, OverwatchKey } from '../../TModules';

// One label per non-`ok` `CardDataState` kind: the card keeps its title and structure and swaps only
// this body, so the wording has to stay state-specific — and a kind added to the union without a
// label here is a compile error rather than a bare key rendered in a card.
// `NotApplicable` is not a kind: it is `insufficientData` under a reason code that means the reading
// does not apply here, which is a different statement from "too little data" and has to stop being
// headed as one (`DataStateMarker.markerLabelKey`). PLA-8828.
type DataStateLabels = Record<
  Capitalize<Exclude<CardDataStateKind, 'ok'>> | 'NotApplicable',
  string
>;

const dataStates: DataStateLabels = {
  Loading: 'Loading',
  TechnicalError: 'Technical Error',
  InsufficientData: 'Insufficient Data',
  NotApplicable: 'Not Applicable',
  NotConfigured: 'Not Configured',
  AwaitingData: 'No Data Yet',
  PermissionRestricted: 'Permission Restricted',
  ConsentRestricted: 'Consent Restricted',
  NoMatchingCustomers: 'No Matching Customers'
};

// `loading` draws a skeleton rather than a marker, so it needs no sentence; `NotConfiguredMissing` is
// the same state when `assembleCard` could name the unservable readings.
type DataStateDetails = Record<
  Capitalize<Exclude<CardDataStateKind, 'ok' | 'loading'>> | 'NotConfiguredMissing',
  string
>;

/* One sentence per non-`ok` state, drawn under the label only where the caller opts in. They are
   deliberately NOT flattened into one message: denied, never set up, not landed yet, fetched but not
   calculable, and a failed request are five different answers to "why is there no number here", and
   a reader who cannot tell them apart cannot tell who to ask. Nothing here promises a recovery or a
   retry that this screen has not verified. PLA-8828. */
const dataStateDetails: DataStateDetails = {
  TechnicalError: 'The request for this data failed, so nothing is shown here.',
  InsufficientData: 'The data was returned but this reading could not be calculated from it.',
  NotConfigured: 'This reading is not set up for this tenant.',
  NotConfiguredMissing: 'Not set up for this tenant: {{missing}}.',
  AwaitingData: 'This reading has not been made available for this tenant yet.',
  PermissionRestricted: 'Your role does not include access to this reading.',
  ConsentRestricted: "This reading is withheld under the customer's consent settings.",
  NoMatchingCustomers: 'No customers match the current selection.'
};

/* The two vocabularies the wire answers "why is there no number here" in: `reason_code`, and
   `calculation_status` where no code came with it. `assembleCard` prefers the code and falls back to
   the status, and neither arrives with a discriminator, so one flat group answers for both — keyed
   by the PascalCase of the code, which is what `reasonCopyKey` derives. A code with no entry draws
   nothing at all rather than the raw identifier, so adding it here is what gives it a sentence.
   The thresholds baked into some codes are not restated: this screen cannot verify that
   `below_d07_min_eligible_transactions_5` still means five. PLA-8828. */
const dataStateReasons = {
  // `reason_code`
  GovernedNonScalarGrain: 'This reading is not a single figure at this level of detail.',
  IndustryOutOfScope: "This reading does not apply to this tenant's industry.",
  BelowD05MinBaselineObservations20:
    'There are too few baseline observations to calculate this reading.',
  BelowD07MinEligibleTransactions5:
    'There are too few eligible transactions to calculate this reading.',
  BelowD07MinTransferTransactions5:
    'There are too few transfer transactions to calculate this reading.',
  BelowD11MinCashWithdrawals5: 'There are too few cash withdrawals to calculate this reading.',
  BelowMinSampleD06: 'The eligible sample is too small to calculate this reading.',
  BelowJ03EngagementOrExposureMinima:
    'Engagement and exposure are below the minimum needed to calculate this reading.',
  J02ResultConfidenceBelow060StaleSourceEvidence:
    'The supporting source evidence is stale, so the result falls below the 0.60 confidence minimum.',
  /* `readiness_suppression_reason` and `readiness_insufficiency_reason`, off the 64-column
     `C-*-PR-*` schema. Only the codes the Canonical Rule Library names for ALL_PRODUCT_READINESS are
     here: the six guardrails its Suppressed state lists, the two Insufficient Data causes, and the
     two state codes themselves. A code outside that list draws NOTHING rather than its identifier,
     which is `components/Intelligence/reasonKey.ts`'s rule for every governed vocabulary on this
     screen. PLA-8828. */
  ReadinessConsentBlocked: 'Consent for this use case does not permit this action.',
  ReadinessComplianceBlocked: 'A compliance guardrail is active and blocks this reading.',
  ReadinessRiskBlocked: 'A high-risk or fraud guardrail is active for this customer.',
  ReadinessEligibilityBlocked: 'An eligibility guardrail is active and blocks this reading.',
  ReadinessContactBlocked: 'A contact restriction is active and blocks this reading.',
  ReadinessPolicyBlocked: 'A policy guardrail is active and blocks this reading.',
  ReadinessSuppressed: 'A mandatory guardrail blocks this readiness result.',
  ReadinessMandatoryInputMissing: 'A mandatory input for this readiness branch is unavailable.',
  ReadinessDataConfidenceLow:
    'Supporting data confidence is below the 0.60 minimum for this reading.',
  ReadinessInsufficientData:
    'A mandatory input is missing, or result confidence is below the 0.60 minimum.',
  // `calculation_status`
  InsufficientData: 'There is not enough data to calculate this reading.',
  NotApplicable: 'This reading does not apply here.',
  EnabledNotAggregated: 'This reading is set up but has not been aggregated yet.'
};

// Period-comparison copy. The direction words are the delta arrow's accessible name — the glyph
// alone announces as punctuation — and `pp`/`pts` are the FR's native-unit suffixes.
// `Favourable`/`Unfavourable` are the assistive-technology-only reading of the sentiment: the
// direction of a move does not carry its business meaning (a rise in at-risk customers is bad), and
// in the design that meaning is marked by hue alone (WCAG 1.4.1 Use of Color).
const delta = {
  Increased: 'Increased',
  Decreased: 'Decreased',
  Unchanged: 'Unchanged',
  Favourable: 'Favourable',
  Unfavourable: 'Unfavourable',
  PercentagePoints: 'pp',
  Points: 'pts'
};

// Per-card freshness. The FR keeps freshness per card ("a page-level timestamp must not replace
// card-specific freshness"), so the relative phrase is composed here rather than through moment's
// `fromNow()`, whose global locale wording ("8 minutes ago") the design does not use.
const freshness = {
  Updated: 'Updated {{elapsed}}',
  DataAsOf: 'Data as of {{elapsed}}',
  JustNow: 'just now',
  MinutesAgo: '{{value}} min ago',
  HoursAgo: '{{value}} hr ago',
  DaysAgo: '{{value}} d ago'
};

// The FR fixes these five section labels for every card explainer. The colon is inside the string
// because its spacing is locale-dependent.
const tooltip = {
  Trigger: 'More about {{title}}',
  Shows: 'Shows:',
  Includes: 'Includes:',
  Read: 'Read:',
  Use: 'Use:',
  Note: 'Note:'
};

// Per-card chrome, keyed by the descriptor key from `registry/cards.ts` so the renderer derives a
// card's copy from the card it is handed. The five explainer parts are the FR's own sections, and
// the shape is typed so a mistyped part name is a compile error rather than a paragraph that
// silently never renders. `Includes` and `Note` are omitted where they add nothing — an empty
// string would render an empty paragraph instead of no paragraph.
// The FR keeps these bodies free of values, formulas, timestamps and methodology: they explain what
// a card is for, and everything that changes with the data is printed by the card itself.
type CardCopy = {
  Title: string;
  /** The line the screens draw under the card title. Optional: most cards are drawn without one. */
  Subtitle?: string;
  Tooltip: {
    Shows: string;
    Includes?: string;
    Read: string;
    Use: string;
    Note?: string;
  };
};

// Screen order (`design/page1-inventory.md § Section → card sequence`), which is registry order.
const cards: Record<string, CardCopy> = {
  // The three Subtitle strings below are read off the mockup exports
  // (design/frames/Intelligence-Suite_Final-589-{27692,30564}.png, which agree), not authored here.
  // No other card carries one: `i18n.exists` drops the line where the key is absent.
  customer_population_activity: {
    Title: 'Customer Population & Activity',
    Subtitle: 'Registered population, eligible coverage and meaningful active usage',
    Tooltip: {
      Shows: 'The size and activity level of the customer base within the selected scope.',
      Includes: 'Total Registered Users, Eligible Customers, Active Users and Active User Rate.',
      Read: 'Compare current activity with the previous period and check whether filters, consent or data requirements reduce the eligible population.',
      Use: 'Identify whether movement is driven by customer-base growth, reduced eligibility or changing engagement.',
      Note: 'Automated system activity does not independently qualify a customer as active.'
    }
  },
  data_trust_integration_health: {
    Title: 'Data Trust & Integration Health',
    Subtitle:
      'Freshness, required-source availability, identity matching and operational reliability',
    Tooltip: {
      Shows:
        'Whether dashboard results are supported by current, complete and correctly connected data.',
      Includes:
        'Data Freshness, Source Coverage, Identity Confidence, Integration Health and applicable reversal-processing health.',
      Read: 'Delayed sources, missing coverage or weak identity matching may reduce the reliability of other card results.',
      Use: 'Validate data quality before making decisions from customer intelligence.',
      Note: 'Missing or unavailable data is not treated as zero.'
    }
  },
  customer_lifecycle_holdings: {
    Title: 'Customer Lifecycle & Product Holdings',
    Subtitle: 'Current lifecycle stage and number of active products held per eligible customer',
    Tooltip: {
      Shows:
        'Where customers sit in the lifecycle and how many active products, accounts, policies, plans or services they hold.',
      Includes: 'Lifecycle Stage Distribution and Product Holding Count Distribution.',
      Read: 'Review movement across New, Active, At-risk, Dormant, Churned and Reactivated stages, together with single- and multi-product relationships.',
      Use: 'Prioritise acquisition, retention, reactivation and cross-sell analysis.',
      Note: 'Each customer appears in only one current lifecycle stage.'
    }
  },
  customer_engagement: {
    Title: 'Customer Engagement',
    Tooltip: {
      Shows:
        'How widely eligible customers engage and whether they use one or multiple available touchpoints.',
      Includes: 'Engagement Rate and Multi-Source Engagement Coverage.',
      Read: 'A higher engagement rate indicates broader participation; higher multi-source coverage indicates engagement across more than one channel or experience.',
      Use: 'Assess audience reach and whether engagement is concentrated or diversified.',
      Note: 'Multi-source engagement does not automatically indicate conversion.'
    }
  },
  // Payment Usage & Risk still keeps the copy authored from the mockups: its wording names measures
  // that card does not render yet, and its rework is Step F.2 of
  // `plan/plan-followup-behind-spec-cards.md` (PLA-8815 D-3).
  // Behaviour Trends is no longer in that state — the card now renders the four metrics the source
  // copy names, so the source block below is applied with it rather than held back. It is the one
  // card of the 26 whose source tooltip carries no `Note`; omitted, never blanked, so
  // `CardInfoTooltip` drops the paragraph.
  customer_behaviour_trends: {
    Title: 'Customer Behaviour Trends',
    Subtitle: 'Momentum, deviation, disengagement risk and habit consistency',
    Tooltip: {
      Shows: 'Whether customer behaviour is improving, declining, unusual or becoming habitual.',
      Includes:
        'Behavioural Momentum, Behavioural Deviation, Disengagement Early Warning and Habit Strength.',
      Read: 'Positive momentum indicates improving activity; high disengagement indicates weakening engagement. High deviation means unusual behaviour, not necessarily negative behaviour.',
      Use: 'Identify retention, investigation or engagement opportunities.'
    }
  },
  engagement_funnel: {
    Title: 'Engagement Funnel',
    // Node 589-30210, the line under the title the card has always been drawn with.
    Subtitle: 'Distinct customers counted once per stage within the selected period',
    Tooltip: {
      Shows: 'Customer progression through the selected campaign or journey.',
      Includes: 'Stage counts, progression rates, drop-off rates and Funnel Completion Rate.',
      Read: 'Focus on the stage with the largest customer loss and compare completion with the previous equivalent period.',
      Use: 'Identify whether improvement is needed in reach, engagement, enrolment, journey completion or outcome fulfilment.',
      Note: 'Only configured and linked stages are included.'
    }
  },
  current_predicted_customer_value: {
    Title: 'Current & Predicted Customer Value',
    Tooltip: {
      Shows: 'The distribution of customers by current and expected future value.',
      Includes: 'Current CLV and Predicted CLV bands.',
      Read: 'Compare trailing 12-month contribution with expected next-12-month value and movement across Low, Medium, High and Very High bands.',
      Use: 'Support retention, service and growth prioritisation.',
      Note: 'The displayed value basis must be stated; predicted value is an estimate, not guaranteed revenue.'
    }
  },
  customer_sentiment_expectations: {
    Title: 'Customer Sentiment & Expectations',
    Subtitle: 'Permitted sentiment signals, expectation gaps and post-reversal engagement impact',
    Tooltip: {
      Shows:
        'How customers currently feel and whether actual experiences meet expected service or benefit outcomes.',
      Includes: 'Sentiment Score and Expectation Gap Score.',
      Read: 'Negative sentiment highlights experience concerns; a higher expectation gap indicates service friction, delayed benefits, failed fulfilment or unresolved issues.',
      Use: 'Prioritise operational correction, service recovery or targeted communication.',
      Note: 'Review supporting themes and evidence before acting.'
    }
  },
  reward_loyalty_intelligence: {
    Title: 'Reward & Loyalty Intelligence',
    Tooltip: {
      Shows:
        'How customers respond to incentives and how effectively rewards, points and reversals are managed.',
      Includes:
        'Reward Sensitivity, Incentive Dependency, Redemption Rate, Earn-Burn Ratio, Fulfilment Delay and applicable reversal measures.',
      Read: 'High sensitivity means incentives influence behaviour; high dependency means engagement may weaken without incentives.',
      Use: 'Improve reward relevance, economics, fulfilment and reversal performance.',
      Note: 'Review fulfilment and reversal issues before planning further incentives.'
    }
  },
  risk_unusual_activity: {
    Title: 'Risk & Unusual Activity',
    // `589-29266`, verbatim. Distinct from the BAND description `589-29271` ("Detect fraud,
    // anomaly, ...") that sits two lines above it inside the same card frame.
    Subtitle:
      'Severity-weighted anomaly score, risk-band distribution and suspicious reversal behaviour',
    Tooltip: {
      Shows:
        'The severity and distribution of suspicious, abnormal or risk-related customer activity.',
      Includes: 'Fraud or Anomaly Score and Risk Segment Distribution.',
      Read: 'Higher scores indicate stronger risk signals; review the contributing drivers and affected population before interpreting the result.',
      Use: 'Prioritise activity requiring investigation or additional controls.',
      Note: 'A high score is an investigation signal, not proof of fraud.'
    }
  },
  income_cashflow_health: {
    Title: 'Income & Cashflow Health',
    // `589-28063` draws a subtitle under both Banking card titles; neither was built.
    Subtitle:
      'Detected income coverage, income stability, cashflow condition and forecast shortfall risk',
    Tooltip: {
      Shows: 'The availability, stability and expected health of customer income and cashflow.',
      Includes:
        'Income Detection Coverage, Income Stability, Cashflow Health and Cashflow Forecast Risk.',
      Read: 'Review whether income patterns are reliable, cashflow is positive or weakening, and customers may face a forecast shortfall.',
      Use: 'Identify financially stable populations and customers needing responsible support or risk-aware engagement.',
      Note: 'Detected income is inferred from permitted data and may not be verified employment income.'
    }
  },
  debt_spending_health: {
    Title: 'Debt & Spending Health',
    Subtitle:
      'Essential/discretionary spend balance, recurring debt obligations and arrears signals',
    Tooltip: {
      Shows:
        'The balance between essential and discretionary spending and the prevalence of repayment pressure.',
      Includes:
        'Essential versus Discretionary Spend Ratio, Debt Obligation Signal Rate and Arrears Signal Rate.',
      Read: 'Higher debt or arrears signals may indicate reduced affordability or financial pressure.',
      Use: 'Review spending flexibility and repayment risk before product or engagement planning.',
      Note: 'These are portfolio intelligence signals, not formal credit assessments.'
    }
  },
  churn_service_experience: {
    Title: 'Churn & Service Experience',
    Tooltip: {
      Shows: 'Subscribers with negative service-experience signals or a high probability of churn.',
      Includes: 'Network or Service Experience Risk Rate and Telco Churn Risk Rate.',
      Read: 'Review network, support, complaint, recharge, billing, usage and sentiment drivers before interpreting churn risk.',
      Use: 'Prioritise operational correction and retention planning.',
      Note: 'A high churn-risk result is predictive, not confirmation that a subscriber will leave.'
    }
  },
  plan_usage_revenue: {
    Title: 'Plan Usage & Revenue',
    Tooltip: {
      Shows:
        'Movement in average subscriber revenue and how customers use their included plan allowances.',
      Includes: 'ARPU Movement and Data or Voice Utilization Rate.',
      Read: 'Review revenue movement, underused plans and customers approaching or exceeding their allowance.',
      Use: 'Identify plan-fit, value and upgrade considerations.',
      Note: 'High utilization may indicate strong value or upgrade need; low utilization may indicate poor plan fit.'
    }
  },
  recharge_upgrade_readiness: {
    Title: 'Recharge & Upgrade Readiness',
    Tooltip: {
      Shows:
        'The consistency of prepaid recharge behaviour and the share of subscribers ready for a plan upgrade.',
      Includes: 'Recharge Regularity Score and Plan Upgrade Readiness Rate.',
      Read: 'Irregular or delayed recharge may indicate declining engagement; upgrade readiness indicates a possible mismatch between usage and current plan.',
      Use: 'Support prepaid retention and responsible upgrade planning.',
      Note: 'Readiness remains subject to affordability, eligibility, service quality and consent.'
    }
  },
  policy_health_claims: {
    Title: 'Policy Health & Claims',
    Tooltip: {
      Shows:
        'Whether policies are active and in good standing, how reliably premiums are paid and the level of claims activity.',
      Includes: 'Policy Active Rate, Premium Payment Health and Claims Frequency or Ratio.',
      Read: 'Review inactive or unhealthy policies, payment-risk patterns and changes in claims exposure.',
      Use: 'Support portfolio-health, payment and service decisions.',
      Note: 'Policy Active Rate is based on policies, not policyholders.'
    }
  },
  renewal_growth_opportunities: {
    Title: 'Renewal & Growth Opportunities',
    Tooltip: {
      Shows:
        'Policyholders at risk of non-renewal and opportunities related to coverage gaps, riders or cross-sell.',
      Includes:
        'Renewal Risk Rate, Coverage Gap Signal Rate and Rider or Cross-sell Readiness Rate.',
      Read: 'Higher risk indicates retention priority; readiness signals indicate potential needs after eligibility and guardrail checks.',
      Use: 'Support renewal, protection-gap and needs-based planning.',
      Note: 'Signals are not formal advice or automatic suitability decisions.'
    }
  },
  wallet_activity_funding: {
    Title: 'Wallet Activity & Funding',
    Tooltip: {
      Shows:
        'How actively customers use their wallet, how regularly they add funds and which features they adopt.',
      Includes: 'Wallet Active User Rate, Top-up Frequency Score and Wallet Feature Adoption Rate.',
      Read: 'Review inactive users, irregular funding and low- or high-adoption wallet features.',
      Use: 'Prioritise activation, funding and feature-adoption opportunities.',
      Note: 'Feature adoption is measured only for customers eligible for that feature.'
    }
  },
  // Kept from the mockups with Behaviour Trends above — see that note (PLA-8815 D-3).
  payment_usage_risk: {
    Title: 'Payment Usage & Risk',
    Tooltip: {
      Shows: 'How reliably payments complete, and how much of the activity carries elevated risk.',
      Includes: 'Payment attempts made through the wallet in the selected period.',
      Read: 'A failure can sit with funding or with routing, and the two need different fixes.',
      Use: 'Clear the failure driver first — it costs customers more than the risk queue does.',
      Note: 'Risk here marks activity for review; it does not block or decide a payment.'
    }
  },
  merchant_category_data_quality: {
    Title: 'Merchant & Category Data Quality',
    // The three subtitles below are verbatim from the cards' own subtitle nodes — `589-28953`,
    // card 2's under `589-28683` and `589-28839`. PLA-8815.
    Subtitle:
      'Merchant identification, classification coverage, confidence and masked-gateway resolution',
    Tooltip: {
      Shows:
        'How reliably transactions are connected to known merchants, categories and payment purposes.',
      Includes:
        'Merchant Identification, Category Coverage, Low-Confidence and Unclassified Rates, confidence, alias matching and gateway-mask resolution.',
      Read: 'High unknown or low-confidence rates may make preference and spending insights incomplete.',
      Use: 'Validate transaction enrichment before using merchant, category or propensity results.',
      Note: 'Unknown transactions remain visible and are not force-classified.'
    }
  },
  merchant_category_preferences: {
    Title: 'Merchant & Category Preferences',
    Subtitle:
      'Merchant affinity, category/reward preference and average eligible spend per active user',
    Tooltip: {
      Shows:
        'Where customers prefer to spend, which merchants they repeatedly use and which reward categories they redeem.',
      Includes:
        'Merchant Affinity, Category Affinity, Reward Preference and Average Spend per Active User.',
      Read: 'Review top merchants and categories by customer, transaction and value share, while keeping unknown shares visible.',
      Use: 'Support partnership, reward and audience-planning decisions.',
      Note: 'Spend preference and reward-redemption preference are separate views.'
    }
  },
  payment_behaviour_cash_usage: {
    Title: 'Payment Behaviour & Cash Usage',
    Subtitle: 'QR adoption, cash dependency, payment intent mix and normalized rail/channel usage',
    Tooltip: {
      Shows:
        'How customers pay, transfer money and rely on cash across available rails and channels.',
      Includes:
        'QR Payment Ratio, Cash Withdrawal Dependency, Transfer versus Spend, P2P versus P2M and Payment Rail or Channel Mix.',
      Read: 'Compare digital-payment adoption, cash reliance and the mix of merchant payments, transfers and other intents.',
      Use: 'Support payment-adoption, education and channel-planning decisions.',
      Note: 'Cash withdrawals do not reveal the final merchant or spending category.'
    }
  },
  // One card, one tooltip: the source copy writes a tooltip per product, which the shipped
  // product-selector card has nowhere to put, so the product it is showing is interpolated instead.
  product_readiness: {
    Title: 'Product Readiness',
    Tooltip: {
      Shows:
        'How many eligible customers are Ready, Potential, Not Ready, Suppressed or have Insufficient Data for {{productName}}.',
      Read: 'Ready customers meet the score, confidence and mandatory guardrail requirements; Potential customers show partial readiness; Suppressed customers have an active blocker.',
      Use: 'Review Ready and Potential populations for further planning or Growth Planner handoff where available.',
      Note: 'Readiness is not approval, underwriting, suitability advice or automatic campaign execution.'
    }
  },
  growth_opportunities_product_interest: {
    Title: 'Growth Opportunities & Product Interest',
    // PLA-8815 `589-29715`: three of this band's four cards draw a subtitle under the title and none
    // was built. Verbatim from the file.
    Subtitle:
      'Active opportunity signals and product-level propensity after eligibility and guardrails',
    Tooltip: {
      Shows:
        'Evidence-backed opportunities and customer readiness for configured products, services or actions.',
      Includes: 'Opportunity Signal Count and Product-Level Propensity Distribution.',
      Read: 'Higher propensity indicates stronger observed readiness, subject to confidence, eligibility and guardrail checks.',
      Use: 'Identify audiences for growth, retention, education, reactivation or service recovery.',
      Note: 'An opportunity or propensity result does not represent product approval.'
    }
  },
  contact_restrictions: {
    Title: 'Contact Restrictions',
    Subtitle:
      'Customers or signals blocked by consent, fatigue, complaint, risk or confidence guardrails',
    Tooltip: {
      Shows: 'How much of the potential customer population is blocked from engagement and why.',
      Includes: 'Suppression Rate and suppression-reason distribution.',
      Read: 'Review restrictions caused by consent, complaints, fatigue, financial stress, risk, fraud, low confidence or policy controls.',
      Use: 'Understand why an opportunity cannot currently be activated and whether the blocker is temporary or mandatory.',
      Note: 'The latest active restriction takes precedence.'
    }
  },
  ready_for_engagement: {
    Title: 'Ready for Engagement',
    Subtitle:
      'Customer readiness and safety for future nudge planning based on weighted signals and hard blockers',
    Tooltip: {
      Shows: 'Whether customers are currently ready and safe to consider for engagement planning.',
      Includes: 'Nudge Readiness Score and applicable hard-blocker status.',
      Read: 'Readiness considers propensity, confidence, recency, channel reachability, consent, fatigue and risk. Any hard blocker forces a not-ready result.',
      Use: 'Decide whether an opportunity should proceed, be delayed, reviewed or excluded.',
      Note: 'Readiness does not select the message, offer or channel.'
    }
  },
  growth_planner_handoff_readiness: {
    Title: 'Growth Planner Handoff Readiness',
    Subtitle:
      'Signals that satisfy every subscription, confidence, consent, access and evidence gate',
    Tooltip: {
      Shows:
        'How many insight signals currently meet all requirements for transfer to Growth Planner.',
      Includes: 'Layer 3 Handoff Eligible Signal count and percentage.',
      Read: 'Eligible signals pass subscription, permission, confidence, evidence, consent and suppression checks.',
      Use: 'Confirm that a valid and actionable population exists before starting a handoff.',
      Note: 'A signal visible in Layer 2 may still be ineligible for handoff.'
    }
  }
};

// Card affordances. `ViewDetailsFor` is the accessible name: a card carries several identical
// `View Details` buttons, so each one names the metric it opens — and it repeats the visible words
// verbatim so voice control still matches them (WCAG 2.5.3 Label in Name).
const actions = {
  ViewDetails: 'View Details',
  ViewDetailsFor: 'View Details: {{label}}',
  Retry: 'Retry'
};

// A distribution band paints its own label onto the fill, written as `Active · 54%` in the design
// library. Only the separator is translatable here: the band name and the share are both data.
const distribution = {
  SegmentLabel: '{{label}} · {{share}}',
  // The column headers Risk & Protection's two distribution tables carry, verbatim from
  // `589-29376` / `589-29377` / `589-29378`. `%` is the file's own header, not a shortened
  // `Share`, and it is translatable because a locale may well head that column differently.
  ColumnLabel: 'Label',
  ColumnValue: 'Value',
  ColumnShare: '%'
};

// Card-level confidence. `Insufficient` is a first-class band because the FR forbids an unscored
// card being published as a low band or a `0%` reading. `WithScore` is punctuation only, so
// interpolating the already-translated band word into it keeps the four band labels reusable.
const confidence = {
  High: 'High confidence',
  Medium: 'Medium confidence',
  Low: 'Low confidence',
  Insufficient: 'Confidence unavailable',
  WithScore: '{{band}} · {{score}}%',
  ReasonLabel: 'Reason:',
  ReasonSeparator: '; '
};

/**
 * PLA-8815 / PLA-8828: the GOVERNED confidence vocabulary the wire carries per metric on BOTH
 * layers, kept as its own group rather than folded into `confidence` above — that one is the card
 * badge's own copy, whose `WithScore` writes the reading as a percentage, and these are the
 * governance flags and reason codes that sit behind a 0.00–1.00 reliability figure.
 *
 * Neither flag sentence promises a retry or a timeframe: a cap means the score was limited by a
 * governed critical reason, and the handoff lines state the 0.60 minimum as a fact about the score.
 *
 * `Reasons` maps `confidence_limiting_reason` CODES to prose — the client catalogue's own governed
 * vocabulary, 36 codes, each carrying the catalogue's Description verbatim. The catalogue also
 * states a templated narrative per code, and those are deliberately NOT here: every one of them
 * needs named parameters (`{subject_name}`, `{required_input_list}`) that no column on the wire
 * carries, and a template rendered with its placeholders showing is worse than the plain sentence.
 * A code outside this list still draws nothing rather than an identifier. Two of the 36 are live on
 * UAT `generic` today (2026-09-08): `IDENTITY_MATCH_PARTIAL` on O-COM-023 and
 * `CRITICAL_SOURCE_UNAVAILABLE` on O-BFSI-001. PLA-8815, PLA-8828.
 */
const confidenceGovernance = {
  Label: 'Confidence',
  Bands: {
    High: 'High',
    Medium: 'Medium',
    Low: 'Low',
    Insufficient: 'Insufficient'
  },
  CapApplied: 'Score limited by a governed critical reason.',
  HandoffBelowMinimum: 'Below the 0.60 confidence minimum for a Growth Planner handoff.',
  HandoffMeetsMinimum: 'Meets the 0.60 confidence minimum for a Growth Planner handoff.',
  Reasons: {
    InputCoverageComplete:
      'All required inputs are available; input completeness does not reduce confidence.',
    InputCoverageHigh:
      'Most required inputs are available and any gaps are not material to the result.',
    InputCoveragePartial:
      'Some required inputs are unavailable, reducing confidence in the result.',
    InputCoverageLow:
      'A material portion of required inputs is unavailable, significantly reducing confidence.',
    CriticalInputMissing: 'A mandatory input needed to support the result is missing.',
    SourceValidated: 'The contributing source data has passed the required validation checks.',
    SourceReliabilityHigh: 'The contributing sources are considered reliable for this calculation.',
    SourceReliabilityLow:
      'One or more contributing sources have lower reliability and reduce confidence.',
    SourceConflict:
      'Contributing sources contain conflicting information that cannot be fully reconciled.',
    SourceUnavailable: 'A required source is unavailable for the current result.',
    CriticalSourceUnavailable: 'A critical source required to support the result is unavailable.',
    DataNearRealTime: 'The result is based on data refreshed close to real time.',
    DataFreshWithinSla: 'The underlying data is within the approved freshness SLA.',
    DataPartiallyDelayed:
      'Part of the underlying data is delayed, reducing the timeliness of the result.',
    DataStale: 'Material underlying data is older than its approved freshness SLA.',
    CriticalSourceStale: 'A critical source is stale enough to cap the reliability of the result.',
    SourceRefreshFailed:
      'A source refresh or ingestion run failed, limiting the reliability of the displayed result.',
    FreshnessUnknown:
      'Freshness cannot be reliably established for one or more contributing inputs.',
    IdentityMatchStrong: 'Customer identity matching is strong across the contributing records.',
    IdentityMatchPartial:
      'Some contributing records could not be matched to customers with strong confidence.',
    IdentityConflict:
      'Conflicting identifiers reduce certainty that contributing records belong to the same customer.',
    EntityMatchStrong:
      'The relevant merchant, account, policy, product or other entity is matched reliably.',
    EntityMatchPartial: 'Some contributing entities are only partially matched.',
    EntityUnmatched: 'Some contributing records cannot be matched to the required entity.',
    CalculationValidated:
      'The deterministic calculation has passed the required validation checks.',
    CalculationReliabilityLow:
      'The deterministic calculation is usable but has a reliability limitation.',
    RuleValidated: 'The business or scoring rule has passed the required validation checks.',
    RuleReliabilityLow: 'The rule-based result is usable but has a reliability limitation.',
    ModelValidated: 'The model used for the result is validated for the intended use case.',
    BaselineReliable:
      'Sufficient and stable historical data is available to form the comparison baseline.',
    BaselineInsufficient:
      'The available baseline is not sufficient to support a strong comparison.',
    BaselineUnstable:
      'The comparison baseline is available but too unstable to support a strong conclusion.',
    HistoryInsufficient: 'There is not enough historical data to support a reliable result.',
    ModelReliabilityLow:
      'The model reliability for the current result is below the preferred level.',
    ProxyInputUsed: 'An approved proxy is used because a preferred direct input is unavailable.',
    SampleSizeLow: 'The result is based on a small eligible sample or cohort, reducing stability.'
  }
};

// Cards drawn from the metric catalog carry no explainer of their own, so they share this one.
const dynamicTiles = {
  Tooltip: {
    Shows: '{{label}}, as published in the Customer Intelligence metric catalogue.',
    Read: 'The figure covers the selected period. Where there is no reading, the card says why instead of showing a number.',
    Use: 'Read it alongside the other cards in this section.'
  }
};

// The card-level heading is the FR's own question. The metric-level variant only qualifies which
// metric is being explained — the explanation sentence itself is composed upstream, never here.
const whyThisResult = {
  Heading: 'Why this result?',
  MetricHeading: 'Why this result? · {{metricName}}'
};

// The engagement funnel. Only the two stat labels and the two stage tags are translatable — the
// stage number, its name and its reading are data. Node 589-30194 draws the number on its own line
// above the name rather than as a `01 · Name` caption, and puts the control on the stage card, so
// neither the caption template nor a connector name has anything left to label.
const funnel = {
  Stats: {
    Progressed: 'Progressed',
    Dropped: 'Dropped'
  },
  Tags: {
    LargestDropOff: 'Largest drop-off',
    JourneyComplete: 'Journey complete'
  }
};

// Page chrome. The suite prefix and the `Industry:` prefix are wrappers around data — the page
// name and the industry name are interpolated so the separator and the colon stay translatable.
// The industry names themselves come from the PLA-8807 permission catalog (`INDUSTRY_DISPLAY`),
// which stays the single industry map.
// The not-configured pair is page copy, not the role editor's: this page is read-only, so it points
// the reader at where the industry is set rather than at what permissions it would unlock.
const header = {
  Title: 'Intelligence Suite/ {{page}}',
  Industry: 'Industry: {{industry}}',
  IndustryNotConfigured: 'Industry not configured',
  IndustryNotConfiguredNote:
    'Set an industry in Settings → Localization to see industry-specific insights.',
  ToggleFilters: 'Filters'
};

// Handoff-readiness gates. A cleared gate is marked by hue alone in the design, so each node also
// carries one of these as its assistive-technology-only status (WCAG 1.4.1 Use of Color).
const gates = {
  Passed: 'Passed',
  NotPassed: 'Not passed',
  Warning: 'Warning',
  Failed: 'Failed'
};

// Accessible names for the chart primitives. A chart is a graphic: with no name it announces
// nothing, and the highlighted column is otherwise marked by opacity and a glow alone (WCAG 1.4.1).
const chart = {
  TrendSeries: 'Trend: {{series}}',
  // The reading a tile trend reveals under the pointer. It is announced from a live region rather
  // than drawn, because the file draws no tooltip — the marker is the whole visible affordance,
  // and a keyboard reader has no pointer to reveal it with.
  PointReading: '{{label}}: {{value}}',
  Columns: 'Columns: {{labels}}',
  // PLA-8815: a multi-series plot reveals every trajectory's reading at ONE bucket, so the announced
  // string names the bucket and then each series — `PointReading` names a single trajectory.
  BucketReadings: '{{bucket}} — {{readings}}',
  SeriesReading: '{{series}} {{value}}'
};

// Section bands, keyed by `IntelligenceSectionKey`. All nine have shipped, so the record is total: a
// section key added to the union without a band here is a compile error, not a bare key on screen.
// `Description` is optional because one band genuinely has none: `589-29081` draws the Product
// Readiness title straight onto its dropdown. Every other band still declares one, and a band that
// wants a description and omits the key renders no line rather than a bare key.
type SectionCopy = { Title: string; Description?: string };

// The industry band is the only one whose copy varies by tenant, so it alone carries nested entries.
// One per industry the file draws a band for; the outer Title/Description stay as the fallback for a
// slug that gains cards before it gains a design.
type IndustrySectionCopy = SectionCopy &
  Record<'Banking' | 'Wallet' | 'Insurance' | 'Telco', SectionCopy>;

const sections: Record<IntelligenceSectionKey, SectionCopy> & {
  industry_insights: IndustrySectionCopy;
} = {
  customer_base_data_health: {
    Title: 'Customer Base & Data Health',
    Description:
      'Track customer population, activity, lifecycle, holdings and data reliability across the selected customer base.'
  },
  behaviour_engagement: {
    Title: 'Behaviour & Engagement',
    // Node 589-30193, the band strip's own line. Respelled British — Figma writes `behavior`, as it
    // does on the Customer Behaviour Trends labels, and every other string in this family is
    // British. The sentence that stood here named transaction frequency, which no card in the band
    // reports.
    Description:
      'Understand how customers interact, progress, drop off and change behaviour across campaigns, journeys and engagement channels.'
  },
  customer_experience_value: {
    Title: 'Customer Experience & Value',
    Description:
      'Review customer value, sentiment, expectations and experience gaps that may affect loyalty and growth.'
  },
  rewards_loyalty: {
    // 589:28257 and 589:28259, both drawn inside the card frame — see `RewardsLoyaltySection`.
    // The description is the file's own subtitle, verbatim and without a full stop.
    Title: 'Rewards & Loyalty',
    Description: 'Reward response, points engagement, fulfilment reliability and reversal health'
  },
  risk_protection: {
    Title: 'Risk & Protection',
    // 589-29271, verbatim including the full stop.
    Description:
      'Detect fraud, anomaly, unusual activity and customer risk patterns that need attention.'
  },
  // The only band whose heading is data-driven: the industry configured for the tenant names it, and
  // each industry names its own band rather than interpolating the permission catalogue's name —
  // `Banking/BFSI` is the `Industry:` chip's wording (`589-30563`), and interpolating it here is what
  // once put `Banking/BFSI Customer Insights` on the band. Three of the four are not
  // `<industry> Customer Insights` at all, so no template reproduces them.
  //
  // All four titles are stated twice over: the Figma band heading, and the `Section` column of the
  // catalogue's `Metrics Mapping with UI FR - Ov` sheet. PLA-8815.
  industry_insights: {
    // Kept as the fallback for an industry that gains cards before it gains a drawn band. Every slug
    // in `INDUSTRY_KEYS` has one today, so nothing reaches this.
    Title: '{{industry}} Customer Insights',
    Description:
      'Reads that only apply to this tenant’s industry, drawn from the industry set in Localization Settings.',
    Banking: {
      // `589-28063`, both verbatim including the full stop.
      Title: 'Banking Customer Insights',
      Description:
        'Understand income, cashflow, debt, spending health and banking product readiness across customers.'
    },
    Wallet: {
      // `2570:4673`, verbatim.
      Title: 'Wallet Customer Insights',
      // NOT the file's subtitle. `2570:4673` carries the Insurance band's line byte-for-byte —
      // policies, claims and renewals, none of which this band reports — so it is a copy-paste in
      // the design, not this band's copy. Held on the generic line until the designer answers
      // rather than printing a sentence about policies over wallet metrics.
      Description:
        'Reads that only apply to this tenant’s industry, drawn from the industry set in Localization Settings.'
    },
    Insurance: {
      // `2561:4246`, both verbatim.
      Title: 'Insurance Policyholder Insights',
      Description: 'Policy health, payment behaviour, claims, renewal risk and growth opportunities'
    },
    Telco: {
      // `2558:3743`, both verbatim.
      Title: 'Telco Subscriber Insights',
      Description: 'Service experience, churn, revenue, utilization, recharge and upgrade readiness'
    }
  },
  payments_transaction_insights: {
    Title: 'Payments & Transaction Insights',
    // Verbatim from `589-28973`, the band description drawn INSIDE the data-quality card frame.
    Description: 'Merchant data quality, affinity, categorized spend and payment-channel behaviour'
  },
  product_readiness_eligibility: {
    // No Description. `589-29081` draws the band title as the first thing inside the card frame and
    // goes straight to the product dropdown beneath it — read three times over the whole 1360x874
    // selection, twice at 103% and once at 111%, with nothing between the two. The sentence that
    // used to sit here was ours, not the file's.
    Title: 'Product Readiness & Eligibility'
  },
  growth_opportunities: {
    Title: 'Growth Opportunities',
    // PLA-8815: the band description `589:32903` draws, verbatim. The sentence that stood here was
    // ours, not the file's.
    Description:
      'Identify high-potential signals, product interest, readiness and customers safe to engage.'
  }
};

// The Product Readiness card names the product it is reporting on inside its tooltip. Until the
// in-card product dropdown ships (`plan-p4 § Step 4.5`) there is no selection to name, so the
// section supplies this phrase and P4 replaces it with the chosen product.
const productReadiness = {
  DefaultProductName: 'the selected product',
  SelectLabel: 'Product',
  SelectPlaceholder: 'Select a product',
  // The FR's five readiness categories. A customer sits in exactly one, so the names double as the
  // distribution's band labels and as the dropdown-driven card's reading.
  Bands: {
    Ready: 'Ready',
    Potential: 'Potential',
    NotReady: 'Not Ready',
    Suppressed: 'Suppressed',
    InsufficientData: 'Insufficient Data',
    Unavailable: 'Unavailable'
  },
  // FR wording, verbatim: readiness is a propensity reading, and both the card and its breakdown
  // have to say so where a reader could otherwise take it for a lending decision.
  Disclaimer: 'Readiness is not approval, underwriting or suitability advice.',
  /* The two populations `4182-9637` draws the card over, named after the product the dropdown holds:
     `count = 0` is the acquisition branch and `count >= 1` the existing-holder one. The frame writes
     them `No Credit Card` and `Existing Card Holder` for its own product, which is this pattern with
     that product's holding noun in it. */
  Branches: {
    NoHolding: 'No {{product}}',
    Holding: 'Existing {{product}} Holder'
  },
  /* The sub-line under each tile naming the whole population, above the two branch lines. */
  TotalPopulation: 'Total'
};

// Headings for the sub-blocks a section composes into a card body. They are card chrome, not payload:
// the labels and figures inside each block arrive with the fixture and are never translated here.
const blocks = {
  EligibleCoverage: 'Eligible coverage',
  ActiveUserRate: 'Active user rate',
  // The unfilled arc of the two Customer Base rings. It is the track a share is read against, not a
  // category the design names, so it is named generically — it surfaces only under the pointer.
  RingTrack: 'Remainder',
  QualifyingActiveEvents: 'Qualifying active events',
  Evidence: 'Evidence',
  LifecycleStageDistribution: 'Lifecycle stage distribution',
  ProductHoldingDistribution: 'Product holding count distribution',
  // `TransactionTrends` and `TransactionFrequencyBands` retired with the trajectory and band blocks
  // they titled: Behaviour Trends was their only reader, and the card no longer draws either block.
  TopDisengagementDrivers: 'Top disengagement drivers',
  HabitEvidence: 'Habit evidence',
  EngagementSourceCoverage: 'Engagement source coverage',
  SentimentScoreDistribution: 'Sentiment score & distribution',
  ExpectationGapDrivers: 'Expectation gap drivers',
  // The caption under the driver ranking, verbatim from `589:27966`. The lead-in is the block's own
  // name because the sentence defines a score, not the ranking above it.
  ExpectationGapScale:
    'Expectation Gap Score: Higher positive scores indicates greater contribution to the expectation gap.',
  // The driver figure is an absolute contribution, not a period comparison. `589:27965` states its
  // unit once in the column heading, so the figures beside each bar carry none of their own.
  ExpectationGapPoints: 'pts',
  // The driver ranking's two column headings, `589:27964` and `589:27965`.
  ExpectationGapDriverColumn: 'Driver',
  ExpectationGapImpactColumn: 'Gap Impact (points)',
  // The three lines of `div.scoretext` (`589:27911`, `589:27913`, `589:27915`) beside the sentiment
  // ring. The band name is the ring's own label, so it is not repeated here.
  SentimentBasis: 'Weighted normalized sentiment',
  SentimentConfidence: 'Confidence',
  SentimentPreviousPeriod: 'Previous period',
  // The two callout panels each card of the band draws (`call good` / `call bad`). Sentence case in
  // the file, unlike the uppercase block headings.
  TopPositiveDrivers: 'Top positive drivers',
  TopBlockers: 'Top blockers',
  // Product Readiness & Eligibility's three block headings and its two guardrail notes, read off the
  // live canvas for `589-29081`. Each note opens with a bold lead-in on the same line as its body,
  // so the two halves are separate keys rather than one string with markup in it.
  ReadinessDistribution: 'Readiness distribution',
  /* `4182-9637` splits the block in two and names each ring's population after the dash. */
  ReadinessDistributionFor: 'Readiness distribution - {{branch}}',
  EligibilitySignals: 'Eligibility signals',
  /* The two reading columns of the signals panel, verbatim off `4182-9637` — abbreviated in the
     frame because the column is ~80px wide, so they are NOT the branch names spelled out. */
  SignalColumnNoHolding: 'No Prod. Holding',
  SignalColumnHolding: 'Prod. Holding',
  /* The Metric List guardrail's own wording: an input that does not apply to one branch says so
     rather than defaulting to 0 or borrowing the Insufficient Data state. */
  NotApplicable: 'Not Applicable',
  CalculationGuardrails: 'Calculation & guardrails',
  // The caption under the readiness ring's figure, which is the eligible base rather than a band.
  EligibleCentreLabel: 'Eligible',
  ObservedShare: '{{share}} observed',
  EligibleBaseLead: 'Eligible Base:',
  EligibleBaseBody: 'distinct product-applicable eligible customers.',
  ScoreLead: 'Readiness Score:',
  ScoreBody:
    'eligibility/fit 25% + observed need 20% + capacity 20% + product gap 15% + relationship/engagement 10% + data confidence 10%.',
  FinalBandsLead: 'Final bands:',
  FinalBandsBody:
    'Ready ≥75 with confidence ≥0.60 and all guardrails; Potential 50–74; Not Ready <50; Suppressed when consent/risk/compliance/policy/contact blocks; Insufficient Data when a mandatory input is missing or confidence <0.60.',
  // Card 1's two `div.dist` blocks. Comment #16 reads "Missing card => Predicted CLV Band
  // Distribution"; the file has since drawn both, as blocks of Current & Predicted Customer Value
  // rather than as cards of their own.
  CurrentClvBandDistribution: 'Current CLV band distribution',
  PredictedClvBandDistribution: 'Predicted CLV band distribution',
  // Rewards & Loyalty's five block headings, verbatim from `589:28262`, `589:28309`, `589:28361`,
  // `718:1866`, `589:28676` and `589:28638`. Stored in the file's OWN case rather than natural case
  // for the label role to uppercase: `FULFILMENT & REVERSAL KPIs` really does carry a lowercase
  // `s` in the file, which a `text-transform` would flatten to `KPIS`, and three of the six are
  // title case to begin with. The band's own heading rule turns the transform off to match.
  RewardResponseDependency: 'REWARD RESPONSE & DEPENDENCY',
  RewardPointsFlow: 'REWARD & POINTS FLOW',
  FulfilmentReversalKpis: 'FULFILMENT & REVERSAL KPIs',
  TierMembershipDistribution: 'Tier Membership Distribution',
  ReversalDistributions: 'REVERSAL DISTRIBUTIONS',
  ProcessingReconciliation: 'PROCESSING & RECONCILIATION',
  // The two sub-columns of the reversal breakdown, `589:28637` and `589:28636`.
  ByEntitlementType: 'By Entitlement Type',
  ByReversalReason: 'By Reversal Reason',
  // The bold lead-in run of `589:28648`, which is one paragraph and not a heading over one — the
  // colon and the run's boldness are the file's, so the colon is carried in the copy.
  ReversalReconciliationEvidence: 'Reversal Reconciliation Evidence:',
  // `589:28308` and `589:28360`, verbatim including their multiplication, division, minus and
  // en-dash glyphs — they are the file's own, not ASCII stand-ins.
  RewardResponseFormula:
    'RSS: normalized lift between incentivized and non-incentivized activity, adjusted for baseline and confidence. IDS: incentivized engagement share × reward-absence drop factor × recurrence factor, scaled 0–100.',
  RewardPointsFlowFormula:
    'Redemption Rate: redeemed rewards ÷ issued rewards × 100. Earn-Burn Ratio: points earned ÷ points burned; net points = earned − burned.',
  // Rewards & Loyalty heads its second whybox `589:28645` differently from Customer Base's
  // `Evidence` (`589:32329`), so it carries its own key rather than widening that one.
  EvidenceAndFreshness: 'Evidence and freshness',
  UnusualActivityPatterns: 'Unusual activity patterns',
  FlaggedShareOfActive: 'Flagged share of the active base',
  FlaggedCustomers: 'Flagged customers',
  FraudAnomalyScore: 'Fraud / anomaly score',
  RiskSegmentDistribution: 'Risk segment distribution',
  EvidenceInputs: 'Evidence inputs',
  // The four Banking block headings, `589-28063`. `panelHeading` uppercases them, so they are
  // written sentence case here as every other block label is.
  // `IncomeStabilityDistribution` used to head the left block of the Income card; the file draws no
  // stability distribution on either card, and the block that stands there is the coverage ring.
  SpendComposition: 'Spend composition',
  DebtArrearsEvidence: 'Debt & arrears evidence',
  IncomeCoverageAndStability: 'Income coverage & stability',
  CashflowHealth: 'Cashflow health',
  ForecastShortfallRisk: 'Forecast shortfall risk',
  // The heading beside the SPEND COMPOSITION ring, and the two lines under it. The amount and the
  // share are both data, so only the punctuation between them is translatable.
  EssentialDiscretionaryHeading: 'Essential ÷ discretionary',
  // `589-28092` is not self-consistent here: at 6x the `Essential` line separates amount from share
  // with a hyphen and the `Discretionary` line directly under it with a middot. One separator has to
  // win, and it is the middot every other paired reading on this page already uses
  // (`Distribution.SegmentLabel`, `ResultConfidence`). Raised with design.
  SpendCompositionLine: '{{label}}: {{amount}} · {{share}}',
  CategorizedCustomers: 'Categorized customers: {{customers}}',
  // The two lines beside the income coverage ring.
  IncomeDetectedCustomers: '{{customers}} income-detected customers',
  ResultConfidence: 'Result confidence: {{score}} · {{band}}',
  IncomeStabilityScoreLine: 'Income Stability Score: {{score}} · {{band}}',
  // The bare band word those two lines end on. Deliberately not `Confidence.High`, which is the
  // badge's full phrase — interpolating it would print `84% · High confidence`.
  BandHigh: 'High',
  BandMedium: 'Medium',
  BandLow: 'Low',
  // The fourth band `CardConfidence['band']` gained with the card confidence. This line has never
  // printed it — its two readings are fixture-backed and both `high` — but the word has to exist
  // for the map to be total, and `Insufficient` is the catalogue's own band name.
  BandInsufficient: 'Insufficient',
  // Each FORECAST SHORTFALL RISK tile captions its rate with the horizon and the customers behind
  // it; the CASHFLOW HEALTH tiles above caption theirs with the band name alone.
  ForecastHorizonCaption: '{{horizon}} · {{customers}} customers',
  EssentialVsDiscretionarySpend: 'Essential vs discretionary spend',
  // Reads as a caption under the ring: the figure at its centre is a ratio, not the share the arc
  // draws, so the card has to say which of the two it is.
  EssentialDiscretionaryFormula:
    'Ratio = essential spend ÷ discretionary spend across the selected period.',
  // The caption sits directly under the printed figure, so it names THAT figure — the ratio — while
  // the arc behind it draws the essential share the note below spells out.
  EssentialDiscretionaryRatio: 'Essential : discretionary ratio',
  // The three formula notes `589-28063` draws, one per block that states a derived figure. Each line
  // is a bold lead-in and a regular body, so the two halves are separate keys rather than one string
  // a renderer would have to split on a colon — a locale is free to punctuate the lead differently.
  SpendCompositionFormulaLead: 'Ratio:',
  SpendCompositionFormulaBody:
    'total essential spend ÷ total discretionary spend using the governed merchant/category taxonomy.',
  IncomeDetectionFormulaLead: 'Income detection:',
  IncomeDetectionFormulaBody:
    'recurring credit amount, source, narration/category and periodicity confidence.',
  IncomeStabilityFormulaLead: 'Stability:',
  IncomeStabilityFormulaBody:
    'regularity, inverse amount variance, source consistency and missed-income periods.',
  CashflowHealthFormulaLead: 'Cashflow Health:',
  CashflowHealthFormulaBody:
    'normalized net cashflow ÷ income/inflow, adjusted by balance trend and obligation ratio.',
  ForecastRiskFormulaLead: 'Forecast Risk:',
  ForecastRiskFormulaBody:
    'customers below the configured forecast threshold ÷ customers with a cashflow model.',
  /* PLA-8815: the panel headings the three new industry bands draw (`2533:1670`). The ten keys that
     stood here named a plan mix, a recharge cadence, a policy mix, a funding-source list and a
     payment-failure list — panels composed when the file drew no mockup for these three bands, and
     which none of them draws. They are replaced rather than added to, because nothing renders them
     any more; leaving them would leave the next reader unable to tell which set is live.

     Sentence case, like every other block heading in this file: the design sets these in caps, which
     is `blockHeading`'s own text-transform and not the string's. `ChurnRiskDrivers` is the one key
     that survives — the telco band still draws that panel under that name. */
  /* PLA-8815 — every heading below is the file's own wording. SEVEN had drifted into a house
     `... by band` / `... by status` phrasing that `2533:1670` never uses; the user caught two of
     them and the audit that followed found five more. The file writes `TELCO CHURN-RISK
     DISTRIBUTION`, `DATA-UTILIZATION BANDS`, `PLAN UPGRADE READINESS BANDS`, `POLICY STATUS
     DISTRIBUTION`, `RIDER / CROSS-SELL READINESS BANDS`, `ANOMALY SEVERITY DISTRIBUTION` and
     `WALLET FEATURE ADOPTION RATE · ADOPTERS / ELIGIBLE`.

     Sentence case here and uppercased by `blockHeading`'s own text-transform, which is why these
     read lower here and draw in caps. */
  ChurnRiskDrivers: 'Top churn drivers',
  /* Five `... by band` keys died here rather than being corrected: each was replaced by the
     population-bearing heading the file actually draws (`...WithSubscribers`, `...WithPolicies`,
     `...WithUsers`, `CoverageGapSignalsEligible`), and a key nothing resolves is dead copy that the
     next reader has to prove unused before touching. PLA-8815. */
  ServiceRiskEvidence: 'Service-risk evidence',
  DataUtilisationBands: 'Data-utilization bands',
  PlanUpgradeReadinessBands: 'Plan upgrade readiness bands',
  UpgradeReadinessEvidence: 'Upgrade-readiness evidence',
  PolicyStatusDistribution: 'Policy status distribution',
  TopRenewalRiskDrivers: 'Top renewal-risk drivers',
  RiderCrossSellReadinessBands: 'Rider / cross-sell readiness bands',
  WalletFeatureAdoption: 'Wallet feature adoption rate · adopters / eligible',
  UseDistributionContributingToDiversity: 'Use distribution contributing to diversity',
  /* PLA-8815 — the panels `2533:1670` draws that the first pass could not: the four composite rings,
     the two panel trends, the reading rows beside them and the two why-questions each of the seven
     cards is headed with. Sentence case like every other heading here; the design sets them in caps,
     which is `blockHeading`'s own text-transform and not the string's.

     The panel headings that name a population carry it as an interpolation rather than in the
     string, because the figure is the payload's and the copy is not: `2558:3744` heads its left
     panel `SERVICE-EXPERIENCE RISK · 1.24M ELIGIBLE SUBSCRIBERS`, and that 1.24M is the same
     population its bands sum to. */

  /* Units, printed beside a bare figure. Interpolated rather than concatenated at the call site so a
     locale can put the unit where its own grammar wants it — and the variable is NEVER `{{count}}`,
     which is i18next's plural SELECTOR: a string in it sends the lookup hunting for `Minutes_one` /
     `Minutes_other`, which do not exist. Every interpolated key in this block names its own
     variable for that reason. PLA-8815. */
  Minutes: '{{minutes}} min',
  PaymentDiversityIndex: 'Payment diversity index',
  DiversityIndexCentreCaption: 'Normalized Shannon entropy',
  AnomalySeverityDistributionWithUsers: 'Anomaly severity distribution · {{users}} users',
  AnomalySignalEvidence: 'Anomaly signal evidence',
  // Each chip names the signal, then the test that raised it and the share it accounts for.
  AnomalySignalChip: '{{signal}} · {{test}} · {{share}}',
  RuleTriggeredAnomalies: 'Rule-triggered anomalies',
  StatisticalAnomalies: 'Statistical/model anomalies',
  NearRealTimeRuleLatency: 'Near-real-time rule latency',
  WalletActiveUserRateTrend: 'Wallet active user rate · weekly trend',
  DistinctActiveWalletUsers: 'Distinct active wallet users',
  EligibleWalletUsers: 'Eligible wallet users',
  PreviousPeriodRateAndCount: 'Previous period',
  PreviousPeriodValue: '{{rate}} · {{users}}',
  QualifyingActivity: 'Qualifying activity',
  QualifyingActivityValue: 'Payment · top-up · transfer · app action',
  /* The ring's centre caption, as `DiversityIndexCentreCaption` is for the ring beside it. Title
     case is the file's own (`2570:4888`), and it does NOT match the sentence case that ring uses
     ("Normalized Shannon entropy", `2570:4713`). The two are shipped as each node draws them rather
     than normalised. Renamed from `TopUpFrequencyScore` when the SubPanel heading it used to title
     was deleted -- the file draws no heading there -- so the name no longer reads as one. PLA-8815. */
  TopUpScoreCentreCaption: 'Top-up Frequency Score',
  MedianTopUpsPerMonth: 'Median top-ups / month',
  MedianTopUpAmount: 'Median top-up amount',
  /* TITLE case, and both of these are ring-hole captions rather than headings. `2561:4290` draws
     `Policy Active Rate` and `2561:4340` draws `Premium Payment Health Score`, both read at 447%.
     They were sentence case while they were panel titles; the case belongs to the ROLE the copy is
     drawn in, and moving a key between roles carries the old role's case with it unless it is
     re-read. The reading rows below stay sentence case because the file draws them that way
     (`Active policies`, `Good-standing checks`) — there is no band-wide rule to apply. PLA-8815. */
  PolicyActiveRate: 'Policy Active Rate',
  ActivePolicies: 'Active policies',
  EligiblePolicies: 'Eligible policies',
  PreviousPeriod: 'Previous period',
  GoodStandingChecks: 'Good-standing checks',
  GoodStandingPassed: 'Passed',
  // Verbatim from `2561:4246`, em dash included: the panel states the counting rule in its own words.
  PolicyEntityNote:
    'Entity is policy—not user or policyholder. Policy counts and policyholder counts are never mixed in this formula.',
  // See `PolicyActiveRate` above: ring-hole caption, title case, `2561:4340` at 447%.
  PremiumPaymentHealthScore: 'Premium Payment Health Score',
  ClaimsFrequencyAndRatio: 'Claims frequency & ratio',
  ClaimCount: 'Claim count',
  ActivePolicyCount: 'Active policy count',
  // The panel prints the division itself rather than the quotient alone.
  ClaimFrequencyFormula: 'Claim frequency',
  ClaimAmountOverPremium: 'Claim amount / premium amount',
  DivisionReading: '{{numerator}} ÷ {{denominator}} = {{result}}',
  RenewalRiskWithPolicies: 'Renewal risk · {{policies}} renewal-due policies',
  HighRiskRenewalPolicies: 'High-risk renewal policies',
  PreviousPeriodRate: 'Previous-period rate',
  CoverageGapSignalsEligible: 'Coverage gap signals · eligible policyholders',
  ServiceExperienceRiskWithSubscribers:
    'Service-experience risk · {{subscribers}} eligible subscribers',
  NetworkEventFreshness: 'Network-event freshness',
  ChurnRiskDistributionWithSubscribers:
    'Telco churn-risk distribution · {{subscribers}} active subscribers',
  PreviousChurnRiskRate: 'Previous churn-risk rate',
  HighRiskMovement: 'High-risk movement',
  HighRiskMovementSubscribers: '{{subscribers}} subscribers',
  ArpuMovementTrend: 'ARPU movement · daily trend',
  TotalBillableRevenue: 'Total billable revenue',
  ActiveSubscribers: 'Active subscribers',
  CurrentArpu: 'Current ARPU',
  BaselineArpu: 'Baseline ARPU',
  ArpuMovementRow: 'Absolute / percentage movement',
  ArpuMovementValue: '{{absolute}} / {{percent}}',
  AllowanceUtilisationDistribution: 'Allowance utilization distribution',
  DataUsed: 'Data used',
  VoiceUsed: 'Voice used',
  DataUsedOverIncluded: 'Data used / included',
  VoiceUsedOverIncluded: 'Voice used / included',
  PetabytesOverPetabytes: '{{used}} PB ÷ {{included}} PB',
  MinutesOverMinutes: '{{used}}B ÷ {{included}}B min',
  RechargeRegularity: 'Recharge regularity',
  MedianRechargeFrequency: 'Median recharge frequency',
  MedianRechargeFrequencyValue: '{{frequency}} / month',
  MedianRechargeAmount: 'Median recharge amount',
  PreviousScore: 'Previous score',
  /* The two questions each card's why-blocks are headed with, keyed by card. `WhyThisResultBox`
     takes the heading as an override, so these replace `Why this result?` rather than sitting above
     it — the file heads every one of the fourteen with a question, not with the generic label. */
  Why: {
    /* EVERY heading names its reading and interpolates the value the card's own tile prints. NONE
       asserts a direction, and that is the rule — applied to all fourteen, not most of them.

       Thirteen of these read `Why did X increase?` / `Why did X decrease?`. That is correct against
       the fixture and correct nowhere else: the moment the seam is live and a reading moves the
       other way, a heading saying `increase` sits over a body explaining a fall. Nothing catches it
       — not a type error, not a failing test, not a visual anomaly, because a sentence that is
       merely untrue still renders.

       `policy_health_claims.Second` was already right and is the precedent this follows; its own
       note states the principle: *a literal here is a second source of truth for it*. A direction
       word is exactly that — a second, static claim about a number the payload owns.

       Naming the reading rather than interpolating the direction is deliberate. A direction could
       be derived from `delta.direction`, but these fourteen use five different grammatical forms
       (`did X decrease`, `is X rising`, `did X improve`, `are X increasing`, `is X stronger`), so
       inflecting them per payload needs a verb table and gets a heading wrong the first time a form
       is added. Naming the reading needs none of that and is unfalsifiable by data. PLA-8815. */
    /* Distinct variable names, because both headings of a card are fed from ONE values bag: a shared
       `{{rate}}` here would have printed the service-experience rate inside the churn question and
       rendered a plausible wrong number with nothing failing. Every other card's pair already
       differs by name; this was the only collision. */
    churn_service_experience: {
      First: 'Why is service-experience risk {{serviceRate}}?',
      Second: 'Why is churn risk {{churnRate}}?'
    },
    plan_usage_revenue: {
      First: 'Why is ARPU {{value}}?',
      Second: 'Why is data utilization {{rate}}?'
    },
    recharge_upgrade_readiness: {
      First: 'Why is recharge regularity {{score}}?',
      Second: 'Why is upgrade readiness {{rate}}?'
    },
    policy_health_claims: {
      First: 'Why is the policy active rate {{rate}}?',
      // Already correct before this pass, and the precedent the other thirteen were changed to
      // match: the score is the same reading the tile and the ring print.
      Second: 'Why is payment health {{score}}?'
    },
    renewal_growth_opportunities: {
      First: 'Why is renewal risk {{rate}}?',
      Second: 'Why is Growth Planner eligibility {{eligible}}?'
    },
    wallet_activity_funding: {
      First: 'Why is the active-user rate {{rate}}?',
      Second: 'Why is the top-up frequency score {{score}}?'
    },
    payment_usage_risk: {
      First: 'Why is payment diversity {{index}}?',
      Second: 'Why is the anomaly rate {{rate}}?'
    }
  },
  PaymentMethodSplit: 'Cash and digital share of transactions',
  PaymentValueBands: 'Transactions by payment value',
  // The band boundaries are amounts, so they are interpolated already formatted in the tenant's
  // reporting currency rather than shipped as bare numbers a reader could take for counts.
  ValueBandUnder: 'Under {{amount}}',
  ValueBandBetween: '{{from}} to {{to}}',
  ValueBandOver: 'Over {{amount}}',
  // PLA-8815: the two panels `589-28836` draws under its metric row, and the formula note inside
  // each. The screen sets the leading term bold and inline with its body, so each note is two keys
  // rather than one — a single string would have to carry markup.
  PaymentIntentMix: 'Payment intent mix',
  PaymentRailChannelMix: 'Payment rail / channel mix',
  // The two rails `O-HYB-005`'s QR ratio splits the card's eligible transactions into: the
  // reading itself and its exact complement.
  QrScanPay: 'QR / scan-pay',
  NonQrRails: 'Other / non-QR',
  // PLA-8815: the two panels `589-28683` draws under its metric row. The view switch over the chart
  // is named by the payload, as the buckets and the series it plots are.
  MerchantAffinityDistribution: 'Merchant affinity distribution',
  // PLA-8828: the frame's own final row (`589-28728`), verbatim. O-HYB-013 names its merchants by an
  // anonymised handle (`mrc_f7260ee6358fbc73`) and carries no trading name in any column, so every
  // unresolved row folds into this one bar rather than printing a handle or inventing a merchant.
  MerchantAffinityUnknownBucket: 'Unknown / low confidence',
  // The same panel when NOTHING resolves — the missing input named, as the catalogue's Missing Data
  // cell requires, rather than one Unknown bar at 100% presenting absent identity as a result.
  MerchantAffinityNoEnrichment:
    'Merchant enrichment is unavailable, so no merchant can be named for these transactions.',
  CategoryAffinity: 'Category affinity',
  // The file draws the chart in its `Spend` state only, so the other view has no readings to plot
  // and says so rather than drawing an empty plot box.
  CategoryAffinityNoReadings: 'No readings for this view yet.',
  // PLA-8815: O-HYB-014 declares a spend view and a reward view of itself, and UAT `generic` answers
  // with the reward one — `dimension_type: category_affinity_reward`, a `calculation_basis` naming
  // "D-07 reward view", and reward-category keys (voucher, badge, cashback). Headed for the view the
  // data is in rather than as `Category affinity`, which is the spend view's name and the name of
  // the block below it. The card's own why-box makes the same distinction.
  RewardCategoryAffinity: 'Reward category affinity',
  // Same bold-term-plus-body shape as the formula notes below, under the affinity list.
  AvgSpendPerActiveUserTerm: 'Average Spend per Active User:',
  AvgSpendPerActiveUserFormula:
    'eligible merchant spend ÷ distinct active customers with eligible merchant spend. Exclude P2P/self-transfers, cash withdrawals, investments, failures, cancellations, refunds and reversals. Recalculate numerator and distinct spenders within each day/time bucket.',
  // PLA-8815: the blocks `589-28951` draws inside the data-quality card. `589-29078` / `589-29077`
  // head the two ring blocks, `589-28995` the reason list; the denominator note is the same
  // bold-term-plus-body shape as the two formula notes above, so it is two keys for the same reason.
  MerchantIdentificationRate: 'Merchant identification rate',
  CoverageBySource: 'Coverage by source',
  CoverageTotalEvents: 'Total events',
  UnclassifiedReasons: 'Unclassified reasons',
  CoverageDenominatorTerm: 'Coverage denominator:',
  CoverageDenominatorNote:
    'eligible card, wallet, QR, regional instant-payment, mobile-wallet, bill-payment and merchant-relevant transfer events. Unknown and low-confidence outcomes remain visible rather than being discarded.',
  CashDependencyTerm: 'Cash dependency:',
  CashDependencyFormula:
    'withdrawal amount/frequency, post-salary pattern, recurring ATM/cash-out and digital-payment offset. Cash is never inferred as an actual merchant category.',
  ChannelBreakdownTerm: 'Optional breakdown:',
  ChannelBreakdownFormula:
    'day of week and tenant-local Morning, Afternoon, Evening or Night. Unknown channels remain visible as UNKNOWN_CHANNEL.',
  // PLA-8815: the block labels the Growth Opportunities band draws, read off `589-29715`.
  // `OpportunitySignalTypes` and `ContactRestrictionReasons` stay because the two BREAKDOWN overlays
  // still use them; neither heads a block on a card any more, because neither block is drawn.
  ProductLevelPropensityDistribution: 'Product-level propensity distribution',
  // Each product cell captions its signal count with its own split and confidence. Middot-separated,
  // as every other paired reading on this page is.
  ProductPropensitySplit: 'High {{high}}% · Medium {{medium}}% · confidence {{confidence}}%',
  SuppressionReasons: 'Suppression reasons',
  // The formula note under the suppression ring. The lead-in is bold and inline with its body, and
  // the separator is an `=` rather than the colon the Banking notes use, so it rides in the body.
  SuppressionRateFormulaLead: 'Suppression Rate',
  SuppressionRateFormulaBody:
    '= suppressed customers or signals ÷ eligible customers or signals × 100. Each reason remains available for evidence.',
  ReadinessComponents: 'Readiness components',
  // The bold run opening the readiness card's evidence strip. Carries its own colon, as the file
  // draws it inline with the list rather than as a heading above one.
  EvidenceInputsLead: 'Evidence Inputs:',
  // Each gate cell states the population that survives it. The LAST gate states the funnel's output
  // instead, which is why the two wordings are separate keys.
  // Not `{{count}}`: that key is i18next's plural selector and takes a number, where these print an
  // already-grouped string.
  HandoffGateRemaining: '{{signals}} remain',
  HandoffGateEligible: '{{signals}} eligible',
  OpportunitySignalTypes: 'Signals by opportunity type',
  ContactRestrictionReasons: 'Why customers cannot be contacted',
  HandoffGates: 'Mandatory handoff gates',
  HandoffBlockers: 'What holds the rest back',
  FunnelSelectedTransition: 'Selected transition',
  // `589:30564`: the subtitle line under the modal title, "Stage: {stage}" — replaces the uppercase
  // "Selected transition" label + stage-name pair with the single plain line the frame draws.
  FunnelStageSubtitle: 'Stage: {{stage}}',
  FunnelTotalDropped: 'Total dropped',
  FunnelDroppedOfEntry: '{{share}} of the {{total}} customers who reached {{stage}}',
  FunnelPrimaryReasons: 'Primary drop-off reasons',
  FunnelChannelCut: 'Drop-off by channel',
  FunnelSegmentCut: 'Drop-off by segment',
  EssentialDiscretionaryUnclassified: 'Unclassified spend',
  OpportunitySignalTotals: 'Signals in the selected period',
  CoverageByEngagementGroup: 'Coverage by engagement group',
  // Reads as a caption under the coverage groups: the groups look like they should sum to more than
  // the base, so the breakdown has to say that a customer lands in exactly one of them.
  CoverageGroupPrecedence: 'Each customer is counted in one group only, by governed precedence.',
  // `589:33976`: the legend's own header row, above the divider.
  CoverageLegendLabel: 'Label',
  CoverageLegendSharePct: '%',
  ActiveSignalsByType: 'Active signals by type',
  ActiveSignalsFreshness: 'Signals by recency',
  ReadyCustomersByReadiness: 'Ready customers by readiness driver',
  HandoffEligibleByMetric: 'Eligible customers by metric',
  // Same caption role as `CoverageGroupPrecedence`, for the blocker split.
  HandoffBlockerPrecedence:
    'Each customer is counted under one blocker only, by governed precedence.',
  ReadyOfEvaluable: 'of {{evaluable}} evaluable customers',
  HandoffOfEvaluated: 'of {{evaluated}} customers evaluated for handoff',
  ReadyByTypeUnavailable: 'Breakdown by opportunity type not available.',
  HandoffByMetricUnavailable: 'Breakdown by metric not available.',
  HandoffBlockerReasons: 'Blocker reasons',
  HandoffBlockerReasonsOverlap:
    'Reasons are not exclusive: a customer can have several blockers, so the counts can add up to more than the blocked total.',
  ReadinessBandDistribution: 'Readiness distribution'
};

// Chrome for the breakdown overlay a card's `View Details` opens. The counting rule a breakdown
// prints under `CountingRuleHeading` is payload, not copy — it is governed per metric and arrives
// with the breakdown, so only the heading above it lives here.
const breakdowns = {
  TabsLabel: 'Breakdown views',
  CountingRuleHeading: 'How this is counted',
  Close: 'Close',
  Context: 'From {{card}}',
  CurrentValue: 'Current value',
  /* The trailing control on every ranked or progress-bar list the six-row cap shortened. The count
     is the number of rows BEHIND the button, not the list length, so the label states what opening
     it adds. Named `hidden` rather than `count` on purpose: `count` is i18next's plural selector and
     would send this key looking for `ViewMore_one` / `ViewMore_other`. PLA-8844. */
  ViewMore: 'View more ({{hidden}} more)',
  // The funnel breakdown's two cuts. The channel and segment names are the FR's own vocabulary, so
  // they are copy rather than fixture labels: the same five channels are reported for every tenant.
  Funnel: {
    ChannelLabel: 'Channel',
    SegmentLabel: 'Segment',
    AllChannels: 'All channels',
    AllSegments: 'All segments',
    Channels: {
      InAppMessage: 'In-app Message',
      PushNotification: 'Push Notification',
      Sms: 'SMS',
      Email: 'Email',
      Others: 'Others'
    },
    Segments: {
      LowEngaged: 'Low-engaged',
      Dormant: 'Dormant',
      New: 'New',
      Active: 'Active'
    }
  }
};

// The Growth Planner handoff: the card CTA, the slide-out that composes the handoff, and the three
// result states. `MetricUnavailable` takes its reason as payload — the reason a metric cannot be
// handed off is governed upstream, not enumerated here.
const handoff = {
  CtaLabel: 'Send to Growth Planner',
  /* PLA-8815: all six conditional-handoff strips lost their copy and then their frames, so every
     CTA on the page is now a bare button and eleven keys went with them.

     This one did NOT go, and the distinction is the whole reason it is named `...Reason` rather
     than `...Note`: it is no longer rendered anywhere on screen. It is the `disabledReason` behind
     the greyed Growth Planner Handoff Readiness CTA, reaching the reader through `aria-describedby`
     alone. Deleting it with the visible note would have left that button explaining itself to
     nobody — a sighted reader at least sees it greyed; a screen-reader reader would get silence. */
  HandoffUnavailableReason:
    'Handoff is unavailable until subscription, permission, consent, confidence and guardrail checks pass.',
  MetricTotal: 'Total',
  MetricEligible: 'Eligible',
  MetricConfidence: 'Confidence',
  MetricUnavailable: 'Unavailable: {{reason}}',
  // The file numbers both slide-out steps: `589:33521` prints `2 · Handoff checks` and `589:33487`
  // `1 · Choose actionable metrics`. The numbering is the step's own, not decoration.
  ChecksHeading: '2 · Handoff checks',
  // The five gates the slide-out evaluates before a handoff may run. The shipped card fixture draws
  // SIX gates, but its last one (`Eligible`) is the funnel's OUTPUT rather than a check — the five
  // here are the conditions that produce it. The count is still open with the design owner (SP-3).
  Checks: {
    // Three of these now carry the file's own wording (589:33524 / 33527 / 33530 / 33533 / 33536),
    // because they name the SAME check this hook already computes. Two of the file's five do not
    // appear: `Growth Planner subscription` and `Evidence attached` have no check behind them here,
    // and `Confidence threshold` / `Data freshness` below are ours and are drawn by no row in the
    // file. They are deliberately NOT relabelled — a row may not claim a gate nothing evaluated.
    Population: 'Minimum cohort size',
    Confidence: 'Confidence threshold',
    Consent: 'Consent and suppression',
    Permission: 'Permission to hand off',
    Freshness: 'Data freshness'
  },
  PanelTitle: 'Send to Growth Planner',
  PanelContext: '{{card}} · {{product}}',
  MetricListHeading: '1 · Choose actionable metrics',
  SelectedCount: '{{count}} metric selected',
  // NOT `{{count}}`: that is i18next's plural trigger AND it renders the raw integer, which put
  // an unseparated `18420 eligible customers` under a panel whose every other figure goes
  // through formatNumber. The file prints `10,000 eligible customers`. The caller formats.
  TotalEligible: '{{customers}} eligible customers',
  TogetherNote: 'Selected metrics are handed off together as one Growth Planner input.',
  Continue: 'Continue to Growth Planner',
  Close: 'Close',
  // The three titles are the Figma frame copy verbatim; the bodies are invented, because the design
  // carries placeholder text in all three (reported under `plan.md` AF-1).
  Result: {
    SendingTitle: 'Sending to Growth Planner…',
    SendingBody: 'This takes a few moments. Keep this panel open until it finishes.',
    SuccessTitle: 'Successfully sent to Growth Planner',
    SuccessBody: 'The selected metrics are now available as a Growth Planner input.',
    FailedTitle: 'Couldn’t send to Growth Planner',
    FailedBody: 'Nothing was sent. Retry the handoff, or close this panel and try again later.'
  }
};

// The eight filter categories of the filter panel: card titles, the two annotation lines and every
// option label, read verbatim off the live Figma file (design/figma-live-read-2026-08-11.md § 2b)
// and corroborated against the eight card exports. Typed by `IntelligenceFilterId` so a category
// added to the union without copy here is a compile error rather than a card of bare keys.
type FilterCategoryCopy = {
  Title: string;
  AppliesTo: string;
  Default: string;
  Options: Record<string, string>;
};

const filterCategories: Record<IntelligenceFilterId, FilterCategoryCopy> = {
  dateRange: {
    Title: 'Date Range',
    AppliesTo: 'All time-based metrics',
    Default: 'Last 30 days, including today',
    Options: {
      last_7_days: 'Last 7 days',
      last_30_days: 'Last 30 days',
      current_month: 'Current month',
      previous_month: 'Previous month',
      custom: 'Custom range'
    }
  },
  lifecycle: {
    Title: 'Lifecycle',
    AppliesTo: 'All population-based metrics',
    Default: 'All',
    Options: {
      all: 'All',
      new: 'New',
      active: 'Active',
      at_risk: 'At-risk',
      dormant: 'Dormant',
      churned: 'Churned',
      reactivated: 'Reactivated'
    }
  },
  purchaseBehaviour: {
    Title: 'Purchase Behaviour',
    AppliesTo: 'Population metrics that use eligible purchase behaviour',
    Default: 'All',
    Options: {
      all: 'All',
      first_time_buyer: 'First-time Buyer',
      frequent_buyer: 'Frequent Buyer',
      occasional_buyer: 'Occasional Buyer',
      lapsed_buyer: 'Lapsed Buyer',
      non_buyer: 'Non-buyer'
    }
  },
  customerValue: {
    Title: 'Customer Value',
    AppliesTo: 'Population and value-based metrics',
    Default: 'All',
    Options: {
      all: 'All',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      very_high: 'Very High'
    }
  },
  engagement: {
    Title: 'Engagement',
    AppliesTo: 'Population and engagement-based metrics',
    Default: 'All',
    Options: {
      all: 'All',
      high_engagement: 'High Engagement',
      moderate_engagement: 'Moderate Engagement',
      low_engagement: 'Low Engagement',
      no_engagement: 'No Engagement'
    }
  },
  productRelationship: {
    Title: 'Product Relationship',
    AppliesTo: 'Population and product/holding-based metrics',
    Default: 'All',
    Options: {
      all: 'All',
      non_holder: 'Non-holder',
      single_product_customer: 'Single-product Customer',
      multi_product_customer: 'Multi-product Customer'
    }
  },
  demographics: {
    Title: 'Demographics',
    AppliesTo: 'All population-based metrics where demographic filtering is permitted',
    Default: 'All permitted demographics',
    Options: {
      all_permitted: 'All permitted demographics',
      age_band: 'Age band',
      gender: 'Gender',
      nationality: 'Nationality',
      income_band: 'Income band',
      ethnicity: 'Ethnicity'
    }
  },
  consentStatus: {
    Title: 'Consent Status',
    AppliesTo: 'Customer-derived metrics, subject to metric-level consent rules',
    Default: 'All',
    Options: {
      all: 'All',
      consented: 'Consented',
      not_consented: 'Not Consented',
      withdrawn: 'Withdrawn'
    }
  }
};

// Panel chrome. `ClearAll` and `ResetToDefault` are two different controls in the design — the bar
// clears every category, the link inside a card resets that card alone — so their wording differs
// deliberately. `PanelLabel` and the four sub-control names are accessible names rather than drawn
// copy: the design draws no visible label for any of them.
const filters = {
  PanelLabel: 'Customer Insights filters',
  AppliesToLabel: 'Applies to:',
  DefaultLabel: 'Default:',
  DefaultHint: 'default',
  ResetToDefault: 'Reset to default',
  ClearAll: 'Clear all filters',
  ComparedToLabel: 'Compared to:',
  ComparisonPeriod: 'Previous {{days}} days',
  CustomRangeStart: 'Start date',
  CustomRangeEnd: 'End date',
  ChipValueWithDetail: '{{option}} · {{detail}}',
  AgeBandFrom: 'Age band from',
  AgeBandTo: 'Age band to',
  DemographicValue: '{{dimension}} value',
  // The two non-ready states of a Demographics sub-select. `Unspecified` is a resolved-but-empty
  // list — nobody has supplied values for that dimension — and states the gap rather than leaving
  // the row visibly short of a control.
  DemographicValueLoading: 'Loading…',
  DemographicValueUnspecified: 'Not configured',
  Categories: filterCategories
};

// Customer 360 Intelligence (PLA-8828). Its own branch rather than more keys on the Customer
// Insights root: the two screens share this module's primitives but not a single string, and the
// page title in particular must never read "Customer Insights" (FR § 6).
//
// The section titles and descriptions are VERBATIM from Frame A's rendered headings
// (`design/figma-spec.md` § 5.2-5.9), so a wording change belongs in the file first. Customer
// Profile carries no Title at all: § 5.1 records that the section opens straight on the identity
// block with no heading pair drawn, and the frame skips the heading when the title is empty.
type Customer360SectionCopy = { Title: string; Description: string };

// The industry band is the only Customer 360 section whose heading varies by tenant, so it alone
// carries nested entries — the same shape Layer 1's `industry_insights` uses. `Banking` has no entry
// because the catalogue gives Banking/BFSI no card in this band, and a heading it can never draw
// would be a fourth title with nothing behind it. PLA-8828.
type Customer360IndustrySectionCopy = Customer360SectionCopy &
  Record<'Wallet' | 'Insurance' | 'Telco', Customer360SectionCopy>;

const customer360Sections: Record<Customer360SectionKey, Customer360SectionCopy> & {
  industry_insights: Customer360IndustrySectionCopy;
} = {
  // Empty rather than absent: the frame skips the heading when the title is empty, and a declared
  // empty string says "this section draws none" where a missing key would read as an oversight.
  customer_profile: { Title: '', Description: '' },
  behaviour_engagement: {
    Title: 'Behaviour & Engagement',
    Description:
      'Understand how the customer behaves, interacts and engages, including recent activity, behavioural patterns and journey progression.'
  },
  customer_experience: {
    Title: 'Customer Experience',
    Description:
      "Understand the customer's sentiment, expectations and experience gaps that may affect their relationship and future engagement."
  },
  rewards_loyalty: {
    Title: 'Rewards & Loyalty',
    Description:
      'Understand how the customer earns, uses and responds to rewards and points, including redemption behaviour, incentive dependency and reversal impact.'
  },
  risk_evidence: {
    Title: 'Risk & Evidence',
    Description:
      'Assess unusual activity and customer risk while reviewing the confidence, evidence and data quality supporting each insight.'
  },
  financial_health_insights: {
    Title: 'Financial Health Insights',
    Description:
      "Understand the customer's income, cashflow, spending, debt obligations, balances and overall financial health."
  },
  payment_insights: {
    Title: 'Payment Insights',
    Description:
      'Understand how the customer pays, transfers and uses cash, including preferred merchants, categories and transaction-data reliability.'
  },
  industry_product_readiness: {
    Title: 'Industry & Product Readiness',
    Description:
      'Assess which relevant products or services the customer appears ready for based on observed need, eligibility, capacity and supporting evidence.'
  },
  /* The three band titles are the catalogue's own `Section` values, verbatim
     (`04-ui-mapping-c360.md`: `Wallet Customer Insights`, `Insurance Policyholder Insights`,
     `Telco Subscriber Insights`) and are the same three strings Layer 1's band already carries. The
     tab states NO section description, and this band has no frame on the Customer 360 screen to read
     one off, so each line below names that band's own two cards and asserts nothing further. */
  industry_insights: {
    // The fallback for an industry that gains a card in this band before it gains a heading. Nothing
    // reaches it today: the section renders only for the three slugs named below.
    Title: '{{industry}} Customer Insights',
    Description: 'Reads that apply only to the industry set in Localization Settings.',
    Wallet: {
      Title: 'Wallet Customer Insights',
      Description:
        "Understand this customer's wallet activity, feature adoption, payment usage and velocity risk."
    },
    Insurance: {
      Title: 'Insurance Policyholder Insights',
      Description:
        "Understand this customer's policy health, premium and claim experience, renewal risk and coverage opportunities."
    },
    Telco: {
      Title: 'Telco Subscriber Insights',
      Description:
        "Understand this customer's plan usage, upgrade readiness, service experience and churn risk."
    }
  },
  growth_opportunities: {
    Title: 'Growth Opportunities',
    Description:
      "Identify the customer's strongest opportunities and product interests, and determine whether they are ready and safe to engage."
  }
};

// --- Customer 360 card copy: Sections 7-9 (Payment Insights / Industry & Product Readiness /
// Growth Opportunities). PLA-8828. Titles, subtitles, block headings and table column headers are
// VERBATIM from Frame A (`design/figma-spec.md` 5.7-5.9), including the US spelling of
// `Payment & Merchant Behavior` and the Figma rename of the catalogue's `Transaction Data Quality`
// to `Merchant & Category Data Quality`. Block headings are given in natural case because the
// heading treatment uppercases them. The explainers follow the FR's five-part shape and, per the
// FR, state no figure, formula or timestamp.
const customer360CardsSections7To9 = {
  payment_merchant_behaviour: {
    Title: 'Payment & Merchant Behavior',
    Subtitle:
      'Merchant affinity, category/reward preference and average eligible spend per active user',
    Tooltip: {
      Shows: 'Where and how this customer pays, and which merchants and categories they favour.',
      Includes:
        'Top Merchant Affinity, Top Spend Category, Average Spend, QR Payments, P2P / P2M Confidence, Active User Average Spend, Transfers vs Spend and merchant alias resolution.',
      Read: 'Compare the customer against the active-user average before reading any single merchant or category share as a preference.',
      Use: 'Choose merchant, category and channel context for an offer this customer is likely to act on.',
      Note: 'Transfers are never inferred as a merchant category.'
    },
    Blocks: {
      MerchantRankingView: 'Merchant ranking view',
      SpendView: 'Spend View',
      RewardView: 'Reward View',
      ReasonCodeBreakdown: 'Reason Code Breakdown (By Transaction Count)',
      TransfersVsSpend: 'Transfers vs Spend',
      Spend: 'Spend',
      Transfers: 'Transfers',
      CategoryAffinity: 'Category Affinity',
      DayTimePreference: 'Day & Time Preference',
      HeatLow: 'Low',
      HeatHigh: 'High',
      // The Reward View state of the card (`3583-15603`) retitles two blocks and draws one tile of
      // its own in place of Spend View's `QR Payments` and `P2P / P2M Confidence`.
      RewardDayTimePreference: 'Reward Day & Time Preference',
      RewardRedemption: 'Reward redemption',
      Redemption: 'Redemption',
      SpendVsActiveUserAverage: 'Spend vs Active User Average',
      RewardVsActiveUserAverage: 'Reward vs Active User Average',
      SpendVsReward: 'Spend vs Reward (by value)',
      Reward: 'Reward',
      Total: 'Total',
      TotalValue: 'Total Value',
      AliasResolution: 'Alias Resolution',
      Resolved: 'Resolved',
      Unresolved: 'Unresolved',
      // `Top Merchants by Value (T12M)` column headings (`3583-8383`).
      Merchant: 'Merchant',
      Amount: 'Amount',
      Share: 'Share',
      Txns: 'Txns',
      Rank: 'Rank'
    }
  },
  cash_usage_dependency: {
    Title: 'Cash Usage & Dependency',
    Subtitle: 'Cash withdrawal behaviour and dependency pattern.',
    Tooltip: {
      Shows: 'How far this customer still depends on cash rather than on digital payment rails.',
      Includes:
        'Cash Withdrawal Dependency Score, Cash Dependency Score, Cash Withdrawals, Post-Income Withdrawals and the payment intent, rail and channel mix.',
      Read: 'A rising dependency score alongside post-income withdrawals points to cash-out behaviour rather than to lower spend.',
      Use: 'Decide whether a digital-payment nudge is appropriate before offering one.',
      Note: 'Cash is never inferred as an actual merchant category, and unknown channels stay visible rather than being dropped.'
    },
    Blocks: {
      PaymentRailMix: 'Payment Rail / Channel Mix',
      // The two rails `C-HYB-004` splits the customer's eligible transactions into. `QR / scan-pay`
      // keeps the wording the frame's own rail row carried (`3583-8501`).
      QrScanPay: 'QR / scan-pay',
      NonQrRails: 'Other / non-QR',
      BalanceImpact: 'Balance Impact (Avg.)',
      RecentWithdrawals: 'Recent Withdrawals',
      LowConfidenceTransactions: 'Low Confidence Transactions (Latest)',
      ImpactOnInsights: 'Impact on Insights',
      EvidenceInputs: 'Evidence Inputs',
      CashWithdrawalTrend: 'Cash Withdrawal Trend',
      // Its own tile beside `Balance Impact (Avg.)` (`3583-8588`), not a row of it.
      DigitalSubstitution: 'Digital Substitution',
      /* The card's two footnote panels (`3583-8774` and its sibling). Each opens on a bold lead-in
         running into its body on one line, so the halves are separate keys rather than markup. */
      CashDependencyLead: 'Cash dependency:',
      CashDependencyNote:
        'withdrawal amount/frequency, post-salary pattern, recurring ATM cash-out and crypto-payment offset. Cash is never inferred as an actual merchant category.',
      OptionalBreakdownLead: 'Optional breakdown:',
      OptionalBreakdownNote:
        'day of week and tenant-local Morning, Afternoon, Evening or Night. Unknown channels remain visible as UNKNOWN_CHANNEL.',
      ViewMore: 'View More'
    },
    Columns: {
      Date: 'Date',
      Channel: 'Channel',
      Amount: 'Amount',
      PostIncome: 'Post-Income',
      DateTime: 'Date / Time',
      Merchant: 'Merchant',
      Confidence: 'Conf.',
      Reason: 'Reason'
    }
  },
  transaction_data_quality: {
    Title: 'Merchant & Category Data Quality',
    Subtitle:
      'Merchant identification, classification coverage, confidence and masked-gateway resolution',
    Tooltip: {
      Shows:
        'How reliably this customer’s transactions can be attributed to a merchant and a category.',
      Includes:
        'Merchant Identification Rate, Category Classification Coverage, Unclassified Transaction Rate, Merchant Alias Match Rate and Gateway Masking Resolution Rate.',
      Read: 'Low identification or coverage reduces the confidence of every merchant and category reading on this customer.',
      Use: 'Validate attribution quality before acting on a merchant or category preference.',
      Note: 'Unknown and low-confidence outcomes remain visible rather than being discarded.'
    },
    Blocks: {
      TotalEvents: 'Total events',
      UnclassifiedReasons: 'Unclassified Reasons',
      EvidenceInputs: 'Evidence Inputs',
      AffectedSummary: 'Affected transactions and value',
      // The two rows beside the identification ring (`3583-7953`).
      TotalTransaction: 'Total Transaction',
      TotalValue: 'Total Value',
      // The bottom strip's three readings (`3583-8076`), which the frame heads uppercase.
      AffectedTransactions: 'Affected Transactions (Low Confidence)',
      AffectedValue: 'Affected Value',
      UnknownShare: 'Unknown Share (By Value)',
      ViewMore: 'View More'
    }
  },
  product_readiness: {
    Title: 'Product Readiness',
    Tooltip: {
      Shows:
        'Whether this customer appears ready for the selected product, and what supports or blocks it.',
      Includes:
        'Readiness Status, the selected product’s readiness score, Affordability Ratio, Delinquency Status and the supporting limit, repayment and guardrail evidence.',
      Read: 'Readiness is an observed-need and capacity signal, not an approval; the guardrail checks decide whether a handoff is permitted.',
      Use: 'Select a product to see whether this customer is ready for it before handing the opportunity on.',
      Note: 'A product with no reading behind it shows its own data state rather than a score.'
    },
    Blocks: {
      CustomerCardHolding: 'Customer Card Holding',
      RepaymentHistory: 'Repayment History (24M)',
      OnTime: 'On-time',
      OtherEligibleCards: 'Other Eligible Card Options',
      EligibilityGuardrails: 'Eligibility & Guardrails',
      CardUtilizationTrend: 'Card Utilization Trend',
      ViewMore: 'View More',
      /* PLA-8828: the one heading of `productReadiness/GenericProductReadinessLayout.tsx`, the body
         a product with no designed frame draws. Deliberately product-neutral — it heads the tenant's
         own catalogue ranked by each product's own readiness reading, and names no product shape. */
      ReadinessAcrossProducts: 'Readiness Across Products',
      // PLA-8828: the block headings of the three industry layouts under
      // `customer360/productReadiness/`, verbatim from the frames named beside each. The
      // Credit Card / Limit Upgrade state above draws none of them.
      // 3583-16045, the no-card branch of Credit Card / Limit Upgrade.
      RecommendedCardBestFit: 'Recommended Card (Best Fit)',
      // 4182-11073 / 4182-11058 / 4182-10833 / 4182-11095 / 4182-10858, Mortgage / Home Loan.
      CurrentHoldingNoMortgage: 'Current Holding — No Mortgage/Home Loan',
      MortgageHandoffRecommendation: 'New Recommendation — Mortgage/Home Loan Handoff',
      HomeBuyingContext: 'Home-Buying Context',
      DownPaymentCapacityEvidence: 'Down-payment Capacity Evidence',
      CapacityAffordabilityContext: 'Capacity / Affordability Context',
      // 4182-11089 and 4182-11288 draw the same heading on the mortgage and insurance frames.
      ContextProductGap: 'Context / Product Gap',
      // 4182-11214, Policy Renewal. The insurance frame heads its donut `Repayment History` with no
      // window in the title, where the card-holding state above draws `Repayment History (24M)`.
      RepaymentHistoryPlain: 'Repayment History',
      // 4182-11251, Policy Renewal.
      PolicyRenewalRecommendation:
        'New Recommendation — Renew + Increase Cover + New Personal/Family Health Policy',
      // 4360-6328, the second selector, drawn only for an insurance tenant.
      PolicySelectLabel: 'Policy',
      /* The reading a CONFIRMED non-holder's product-specific fields take. Distinct from
         `DataStates.NotConfigured` and from `InsufficientData`: nothing is missing and nothing is
         unconfigured — the question does not arise for a customer who holds no such product. */
      NotApplicable: 'Not Applicable'
    },
    // `Other Eligible Card Options` is a four-column table in 3583-10957; the frame heads it
    // uppercase. PLA-8828.
    Columns: {
      Card: 'Card',
      KeyBenefits: 'Key Benefits',
      BestValue: 'Best Value',
      WhyEligible: 'Why Eligible?'
    }
  },
  customer_opportunities_product_interest: {
    Title: 'Customer Opportunities & Product Interest',
    Subtitle: 'Active, evidence-backed opportunities with propensity and guardrails.',
    Tooltip: {
      Shows:
        'The opportunities currently open for this customer and how strongly each is supported.',
      Includes:
        'Active Opportunities, Eligible Opportunities, product-level propensity and the usage, behaviour and transaction evidence behind them.',
      Read: 'An opportunity can be active without being eligible; propensity ranks interest, guardrails decide eligibility.',
      Use: 'Pick the opportunity with the strongest evidence before opening a Growth Planner handoff.'
    },
    Blocks: {
      UsageAndBehaviour: 'Usage & Behaviour Evidence',
      RecentTransactions: 'Recent Transactions (Latest 3)',
      ModelFactors: 'Model Factors',
      // Accessible name for the radio 3583-7718 draws on each row; the visible label is the
      // opportunity beside it, which the name repeats verbatim (WCAG 2.5.3 Label in Name).
      SelectOpportunity: 'Select opportunity: {{label}}',
      ViewMore: 'View More'
    },
    Columns: {
      Opportunity: 'Opportunity',
      // Abbreviated in the frame, and drawn as `Prop.` rather than `Propensity`.
      Propensity: 'Prop.',
      Date: 'Date',
      Amount: 'Amount',
      Merchant: 'Merchant',
      Category: 'Category',
      Status: 'Status'
    }
  },
  ready_for_engagement: {
    Title: 'Ready for Engagement',
    Subtitle: 'Overall readiness for safe and responsible engagement now.',
    Tooltip: {
      Shows: 'Whether this customer can be engaged now, and through which channel and window.',
      Includes: 'Readiness Score, handoff status and the recommended channel and time window.',
      Read: 'Readiness answers whether to engage at all; it does not select the offer.',
      Use: 'Confirm readiness before scheduling any outbound contact.'
    },
    Blocks: {
      ReadinessTrend: 'Readiness trend',
      Status: 'Status',
      /* `C-COM-020`'s four governed states, named by its own `calculation_basis`: "bands Ready>=70 /
         Review 40-69 / Not Ready<40", with "any active J-09 hard blocker ... overrides to blocked".
         The wire states which one applies in `value_state`; these are its words. PLA-8828. */
      States: {
        Ready: 'Ready',
        Review: 'Review',
        NotReady: 'Not Ready',
        Blocked: 'Blocked'
      }
    }
  },
  contact_restrictions: {
    Title: 'Contact Restrictions',
    Subtitle: 'Shows whether this customer may be contacted/nudged and the governing reason.',
    Tooltip: {
      Shows: 'Whether this customer may currently be contacted, and what governs that decision.',
      Includes:
        'Contactability status, the governing restriction, its effective and review dates, the channels affected and the consent, complaint, fatigue, risk and policy states.',
      Read: 'A restriction is a hard guardrail: it overrides readiness and propensity for as long as it stands.',
      Use: 'Check this before any outbound contact, whatever the other cards recommend.',
      Note: 'This card is not scored, so it carries no confidence badge.'
    },
    Blocks: {
      GoverningReason: 'Governing reason',
      RestrictionHistory: 'Restriction History (Latest 3)',
      KeyStates: 'Key States',
      ViewMore: 'View More',
      /* `C-COM-021` answers in `value_state` alone — no `metric_value` — and its `calculation_basis`
         names the vocabulary: the "latest governed DNN event state", `not_blocked` where the
         customer carries no active blocker. PLA-8828. */
      States: {
        NotBlocked: 'Not Blocked',
        Blocked: 'Blocked'
      }
    },
    Columns: {
      Date: 'Date',
      State: 'State',
      Reason: 'Reason'
    }
  }
};
// --- end Sections 7-9 ---

// --- Customer 360 card copy: Sections 1-3 (Customer Profile / Behaviour & Engagement / Customer
// Experience). PLA-8828. Titles, subtitles and block headings are VERBATIM from Frame A
// (`design/figma-spec.md` 5.1-5.3). Its `Lorem Ipsum` labels are what the design draws rather than
// slips introduced here, and the panels that carried them no longer render: a tile with no source
// draws its data state, so these keys stand unused until one exists. PLA-8828.
const customer360CardsSections1To3 = {
  customer_details: {
    Title: 'Customer Details',
    Tooltip: {
      Shows: "The customer's recorded profile, address, contact and custom attributes.",
      Read: 'Each row is a stored attribute; a withheld one keeps its label and says it is restricted.',
      Use: 'Confirm who you are looking at before acting on any other card.'
    },
    Blocks: {
      Identity: 'Identity',
      Address: 'Address',
      Contact: 'Contact',
      CustomFields: 'Custom Fields',
      PermissionsAvailability: 'Permissions & Availability',
      ViewMore: 'View More'
    },
    Fields: {
      Identity: {
        1: 'Date of Birth',
        2: 'Gender',
        3: 'Nationality',
        4: 'Marital Status',
        5: 'Tenure',
        6: 'Lorem Ipsum',
        7: 'Lorem Ipsum',
        8: 'Lorem Ipsum'
      },
      Address: {
        1: 'Current Address',
        2: 'Permanent',
        3: 'Region',
        4: 'Pin Code',
        5: 'Geo Coordinates'
      },
      Contact: {
        1: 'Email',
        2: 'Mobile',
        3: 'Alternate Mobile',
        4: 'Preferred Channel',
        5: 'Reachable',
        6: 'Landline'
      },
      CustomFields: {
        1: 'Referral Code',
        2: 'Lorem Ipsum',
        3: 'Lorem Ipsum',
        4: 'Lorem Ipsum',
        5: 'Lorem Ipsum',
        6: 'Lorem Ipsum',
        7: 'Lorem Ipsum',
        8: 'Lorem Ipsum'
      }
    }
  },
  holdings_customer_value: {
    Title: 'Holdings & Customer Value',
    Tooltip: {
      Shows: 'What the customer holds with you and what that relationship is worth.',
      Read: 'The trend plots current against predicted lifetime value; the ring splits holdings by product family.',
      Use: 'Size the relationship before choosing an offer or a retention action.'
    },
    Blocks: {
      CurrentClv: 'Current CLV - Trailing 12M',
      PredictedClv: 'Predicted CLV - Next 12M',
      ValueBand: 'Value Band',
      CustomerValueTrend: 'Customer Value Trend',
      TrailingRange: 'Trailing 12M',
      HoldingsByProductFamily: 'Holdings by Product Family',
      ValueDrivers: 'Value drivers',
      StatusActive: 'Active',
      StatusClosed: 'Closed',
      // `C-COM-003.value_state`, keyed by the governed band it answers with.
      Bands: {
        Low: 'Low',
        Medium: 'Medium',
        High: 'High',
        VeryHigh: 'Very High'
      },
      Drivers: {
        HigherDeposits: 'Higher deposits',
        ActiveCardUsage: 'Active card usage',
        InactivityRisk: 'Inactivity risk',
        RateSensitivity: 'Rate sensitivity'
      },
      Table: {
        Product: 'Product',
        Type: 'Type',
        // The Figma file spells the header `Product Farray`; the wire column is `product_family`
        // and the frame's typo is not reproduced. PLA-8828.
        ProductFamily: 'Product Family',
        Status: 'Status',
        StartDate: 'Start Date'
      }
    }
  },
  profile_summary: {
    SectionTitle: 'Profile & Value',
    SectionSubtitle:
      'Core profile details, product holdings, lifecycle stage, permissions, value, and financial status.',
    Title: 'Profile Summary',
    Tooltip: {
      Shows:
        'Who this customer is, how confidently they are identified, and where they sit in their lifecycle.',
      Read: 'The chip states identity confidence; the rail marks the stages reached so far.',
      Use: 'Open every other section from this anchor.'
    },
    Blocks: {
      Active: 'Active',
      Verified: 'Verified',
      PerxId: 'Perx ID',
      Identifier: 'Identifier',
      AlternateId: 'Alternate ID',
      Joined: 'Joined',
      CurrentTier: 'Current Tier',
      LifecycleStage: 'Lifecycle Stage',
      Lifecycle: {
        NewCustomer: { Name: 'New Customer', Caption: 'First recorded activity' },
        ActiveCustomer: { Name: 'Active Customer', Caption: 'Present' },
        AtRisk: { Name: 'At-risk', Caption: 'If 30d inactive' },
        Dormant: { Name: 'Dormant', Caption: '3-5 months' },
        Churned: { Name: 'Churned', Caption: '6+ inactive months or explicit closure' },
        Reactivated: { Name: 'Reactivated', Caption: 'Activity resumed' }
      }
    }
  },
  customer_behaviour_profile: {
    Title: 'Customer Behaviour Trends',
    Subtitle: 'Momentum, deviation, disengagement risk and habit consistency',
    Tooltip: {
      Shows: "How the customer's activity is moving against their own baseline.",
      Read: 'Each score is out of 100; the drivers rank what is pulling engagement down.',
      Use: 'Decide whether to nudge, reward or leave the customer alone.'
    },
    Blocks: {
      HabitEvidence: 'Habit evidence',
      ObservedTenure: 'Observed tenure'
    }
  },
  engagement_timeline: {
    Title: 'Engagement Funnel',
    Subtitle: 'Distinct customers counted once per stage within the selected period',
    Tooltip: {
      Shows: 'How far the customer travels from notification to redeemed outcome.',
      Read: 'Each stage counts the customer once; the ribbon narrows by the progressed share.',
      Use: 'Find the stage that loses them and act on that stage.'
    },
    Blocks: {
      EngagementSourceMix: 'Engagement Source Mix',
      Sources: {
        App: 'App',
        Email: 'Email',
        PushNotification: 'Push Notification',
        Microsite: 'Microsite',
        Sms: 'SMS',
        OtherNotifications: 'Other Notifications'
      }
    }
  },
  sentiment_expectations: {
    Title: 'Customer Sentiment & Expectations',
    Subtitle: 'Permitted sentiment signals, expectation gaps and post-reversal engagement impact',
    Tooltip: {
      Shows:
        'Permitted sentiment signals and the gap between what the customer expected and received.',
      Read: 'A higher expectation gap is worse; the drivers rank what contributes to it.',
      Use: 'Resolve the largest gap driver before offering anything new.'
    },
    Blocks: {
      UnresolvedIssues: 'Unresolved Issues',
      OpenCount: '{{value}} Open',
      ViewMore: 'View More',
      Severity: { High: 'High', Medium: 'Medium', Low: 'Low' },
      Issues: {
        LoanPreApproval: 'Loan pre-approval delay - SLA breached - 5 days',
        RewardNotCredited: 'Reward not credited - waiting for provider - 2 days',
        CallbackNotReceived: 'Callback not received - pending - 1 day'
      },
      SourceCoverage: 'Source Coverage',
      Sources: {
        AppDigitalFeedback: 'App & Digital Feedback',
        SupportComplaints: 'Support & Complaints',
        Surveys: 'Surveys (NPS/CSAT/CES)',
        OthersPermittedSocial: 'Others (Permitted Social)'
      },
      IssueBreakdown: 'Issue Breakdown',
      IssueTypes: {
        SlaBreach: 'SLA Breach',
        Open: 'Open',
        InProgress: 'In Progress',
        Resolved: 'Resolved'
      },
      Total: 'Total',
      ExpectationGapDrivers: 'Expectation Gap Drivers',
      DriverColumn: 'Driver',
      GapImpactColumn: 'Gap impact (points)',
      SupportingEvidence: 'Supporting Evidence',
      Evidence: {
        SurveysNps: 'Surveys / NPS',
        RewardFulfillment: 'Reward / fulfillment',
        Complaints: 'Complaints',
        SocialMarTech: 'Social / MarTech',
        SupportInteractions: 'Support interactions'
      },
      RecentEvidence: 'Recent Evidence',
      EvidenceColumns: {
        DateTime: 'Date/Time',
        Source: 'Source',
        Type: 'Type',
        Details: 'Details',
        Sentiment: 'Sentiment',
        Impact: 'Impact'
      },
      Sentiment: { Positive: 'Positive', Neutral: 'Neutral', Negative: 'Negative' },
      Impact: { High: 'High', Medium: 'Medium', Low: 'Low' },
      TopPositiveDrivers: 'Top positive drivers',
      TopBlockers: 'Top blockers',
      CustomersAffected: '{{value}} customers affected',
      PositiveDrivers: {
        Driver1: 'Lorem Ipsum',
        Driver2: 'Lorem Ipsum',
        Driver3: 'Lorem Ipsum'
      },
      Blockers: {
        Blocker1: 'Lorem Ipsum',
        Blocker2: 'Lorem Ipsum',
        Blocker3: 'Lorem Ipsum'
      }
    }
  }
};
// --- end Customer 360 card copy: Sections 1-3 ---

// --- Customer 360 card copy: Sections 4-6 (Rewards & Loyalty / Risk & Evidence / Financial Health) ---
// Titles and subtitles are VERBATIM from Frame A (`design/figma-spec.md` § 5.4-5.6). The explainers
// follow the FR's five-part shape and, per the FR, state no figure, formula or timestamp.
//
// `Data Confidence & Supporting Evidence` carries no entry: Figma draws no card for it, only the
// `Evidence by Category` panel inside Risk & Unusual Activity (`notes.md` § 5.1), and that panel is
// headed from its own payload.
const customer360CardsSections4To6 = {
  rewards_loyalty_activity: {
    Title: 'Rewards & Loyalty Activity',
    Tooltip: {
      Shows:
        'How this customer earns, redeems and loses rewards, and how much of their activity depends on being incentivised.',
      Includes:
        'Reward sensitivity and incentive dependency, redemption and earn/burn flow, rewards issued, redeemed, expired and reversed, upcoming expiry, reversal reasons and the entitlement timeline.',
      Read: 'High sensitivity with high dependency means the activity is bought rather than habitual; read the reversal reasons before treating a redemption fall as disengagement.',
      Use: 'Decide whether an incentive is needed at all, and which reward type this customer actually converts.',
      Note: 'Reversed and expired entitlements are reported separately and never netted into redemptions.'
    }
  },
  risk_unusual_activity: {
    Title: 'Risk & Unusual Activity',
    Subtitle:
      'Severity-weighted anomaly score, risk-band distribution and suspicious reversal behaviour',
    Tooltip: {
      Shows: 'Whether this customer shows unusual or suspicious activity, and how serious it is.',
      Includes:
        'Fraud / Anomaly Score, affected events in the last 30 days, the latest and most severe event, the score contributors and the decisions the activity touches.',
      Read: 'The score weights severity, so a single high-severity event can outrank several minor ones; the contributor list ranks signals, it does not decompose the score.',
      Use: 'Decide whether to hold an engagement or a handoff until the flagged activity has been reviewed.',
      Note: 'A flagged event is an observation to review, not a confirmed fraud finding.'
    }
  },
  financial_health: {
    Title: 'Financial Health',
    Tooltip: {
      Shows:
        "This customer's income, cashflow, obligations, balances and overall financial stress.",
      Includes:
        'Monthly income and net cashflow, debt and obligation ratio, account balance, cashflow strength, financial stress score, spend breakdown, debt items and income sources.',
      Read: 'Cashflow strength and the stress score read the same months from different angles; a strong surplus with a rising obligation ratio is still a deteriorating position.',
      Use: 'Size an offer to what this customer can actually afford, and avoid engaging one under financial stress.',
      Note: 'Figures are observed from transaction and account activity, not from a declared income.'
    }
  }
};
// --- end Customer 360 card copy: Sections 4-6 ---

/* --- Customer 360 card copy: the tenant-industry band (Wallet / Insurance / Telco). PLA-8828.
   Quoted from the `Metrics Mapping with UI FR - C` tab, not authored: `Shows` is the card's own
   `What It Means to the User` rows joined, `Includes` its `Metric / Display Label` values verbatim,
   `Read` its `Display & Interaction Guideline` and `Note` the card's own
   `Missing Data, Confidence & Consent Behaviour` rule condensed to the sources it names. `Use` is
   the one part the tab has no column for and is written to the FR's five-part shape, stating no
   figure, formula or timestamp.

   No `Subtitle` on any of the six. Every other Customer 360 card's subtitle is a string read off
   Frame A, and this band has no frame on the screen — an authored line here would be the only
   subtitle on the page that no design states. `InsightCard` drops the slot when the key is absent. */
const customer360CardsIndustryBand = {
  payment_usage_risk: {
    Title: 'Payment Usage & Risk',
    Tooltip: {
      Shows:
        "Diversity of this customer's payment usage across merchants, categories, channels and transaction types, and any abnormal velocity or suspicious wallet behaviour.",
      Includes: 'Payment Diversity and Wallet Velocity Risk.',
      Read: 'Read the diversity reading as a count of rails in use, not as a preference between them; the velocity reading is an observation to review, not a confirmed finding.',
      Use: 'Decide whether a payment or wallet nudge is appropriate for this customer before offering one.',
      Note: 'Without payment, merchant-category or channel data the card states Insufficient Data and names the missing source; it never shows a zero in its place.'
    }
  },
  wallet_activity_adoption: {
    Title: 'Wallet Activity & Adoption',
    Tooltip: {
      Shows:
        "This customer's current wallet engagement strength, the direction and magnitude of their wallet balance movement, and their readiness to adopt a further wallet feature.",
      Includes: 'Wallet Activity Score, Wallet Balance Trend and Next Feature Propensity.',
      Read: 'Read each score with its plain-language band and the comparison period beside it; the propensity names the feature it is about, so it is not a single ranking of the customer.',
      Use: 'Choose which wallet feature to put in front of this customer, and hand the card off only when the readiness supports it.',
      Note: 'Without wallet balance, top-up, payment, transfer, cash-out or app-activity data the card states Insufficient Data and names the missing source; it never shows a zero in its place.'
    }
  },
  policy_health_claims: {
    Title: 'Policy Health & Claims',
    Tooltip: {
      Shows:
        "This customer's policy status and good-standing health, their risk of a late or missed premium payment, and their claim experience.",
      Includes: 'Policy Status / Health, Premium Payment Risk and Claim Experience Score.',
      Read: 'Read the status first and the two scores against it: a policy in good standing can still carry payment risk, and a claim score reads frequency, settlement delay and complaints together.',
      Use: 'Decide whether this customer needs a service intervention before any commercial conversation.',
      Note: 'Without policy, premium, coverage, renewal, lapse or claim data the card states Insufficient Data and names the missing source; it never shows a zero in its place.'
    }
  },
  renewal_coverage_opportunities: {
    Title: 'Renewal & Coverage Opportunities',
    Tooltip: {
      Shows:
        "This customer's non-renewal or lapse risk for policies approaching renewal, their inferred coverage gap, and their readiness band for a rider or add-on.",
      Includes: 'Renewal Risk, Coverage Gap Signal and Rider Readiness Band.',
      Read: 'Read the renewal window and any active blockers alongside the risk score; a readiness band is an eligibility position, never a guaranteed outcome or an automatic action.',
      Use: 'Time a renewal or coverage conversation, and hand the card off only where the readiness band supports it.',
      Note: 'Without policy status, renewal date, premium-payment health, claims experience or complaint and sentiment data the card states Insufficient Data and names the missing source; it never shows a zero in its place.'
    }
  },
  churn_service_experience: {
    Title: 'Churn & Service Experience',
    Tooltip: {
      Shows:
        "This customer's service risk from network quality, complaints, failed recharges, failed activations or negative sentiment, and their churn risk.",
      Includes: 'Service Experience Risk and Telco Churn Risk.',
      Read: 'Read the two together: service risk is what the customer has experienced, churn risk is what that experience plus their usage and billing predicts.',
      Use: 'Decide whether to resolve a service issue before any retention or upgrade offer reaches this customer.',
      Note: 'Without network-quality, complaint, support-interaction or recharge-failure data the card states Insufficient Data and names the missing source; it never shows a zero in its place.'
    }
  },
  plan_usage_upgrade_readiness: {
    Title: 'Plan Usage & Upgrade Readiness',
    Tooltip: {
      Shows:
        'How far this customer uses their current telco plan allowance, how long it has been since their expected or last recharge, and their readiness band for a plan upgrade or add-on.',
      Includes: 'Plan Utilization, Recharge Gap Days and Upgrade Readiness Band.',
      Read: 'Read utilization against the plan it is taken over rather than as an absolute, and the recharge gap against this customer’s own previous gap.',
      Use: 'Size a plan or add-on offer to the allowance this customer actually consumes, and hand the card off only where the readiness band supports it.',
      Note: 'Without usage, allowance, plan, renewal, eligibility or billing data the card states Insufficient Data and names the missing source; it never shows a zero in its place.'
    }
  }
};
// --- end Customer 360 card copy: the tenant-industry band ---

// One branch per Customer 360 card, assembled from the per-slice blocks above so concurrent build
// slices each add exactly one spread rather than editing a shared literal.
const customer360Cards = {
  ...customer360CardsSections1To3,
  ...customer360CardsSections4To6,
  ...customer360CardsSections7To9,
  ...customer360CardsIndustryBand
};

// --- Customer 360 block copy: Sections 4-6 (Rewards & Loyalty / Risk & Evidence / Financial Health) ---
// The in-card block headings and table column headers, VERBATIM from Frame A
// (`design/figma-spec.md` § 5.4-5.6). Given in their natural case because `blockHeading` uppercases
// them. Card titles and explainers live in `customer360CardsSections4To6` above.
const customer360Sections456 = {
  RewardsLoyalty: {
    Blocks: {
      RewardSensitivity: 'Reward sensitivity & dependency',
      RewardPointsFlow: 'Reward & points flow',
      // The remainder of the redemption dial's own arc — issued rewards that were not redeemed.
      Unredeemed: 'Unredeemed',
      ReversalDistributions: 'Reversal distributions',
      PointTierMovement: 'Point & tier movement',
      // The segmented toggle inside the movement panel.
      PointsView: 'Points',
      TierProgressionView: 'Tier Progression',
      RecentEntitlementTimeline: 'Recent Entitlement & Reversal Timeline',
      TimelineColumns: {
        DateTime: 'Date/Time',
        Type: 'Type',
        Entitlement: 'Reward/Entitlement',
        Status: 'Status',
        PointsValue: 'Points/Value',
        Source: 'Source',
        Details: 'Details'
      },
      EvidenceAndFreshness: 'Evidence and freshness',
      ViewMore: 'View More'
    }
  },
  RiskEvidence: {
    Blocks: {
      FraudAnomalyScore: 'Fraud / anomaly score',
      RecentAffectedEvents: 'Recent Affected Events',
      EventColumns: {
        DateTime: 'Date/Time',
        EventType: 'Event Type',
        Channel: 'Channel',
        Amount: 'Amount',
        Status: 'Status',
        RiskReason: 'Risk Reason',
        // Frame A heads these as two columns; the timeline pairs them in one cell.
        SeverityReview: 'Severity / Review Status'
      },
      EvidenceInputs: 'Evidence inputs',
      EvidenceColumns: {
        Category: 'Category',
        Completeness: 'Completeness',
        Status: 'Status',
        DeltaPoints: 'Δ pts'
      },
      ViewMore: 'View More',
      // 3583-9491 draws this one lowercase, unlike its two View More siblings in the section.
      ViewMoreLower: 'View more'
    }
  },
  FinancialHealth: {
    Blocks: {
      SpendBreakdown: 'Spend Breakdown',
      FinancialHealthInsights: 'Financial Health Insights',
      StressFactors: 'Financial Stress Factors',
      CashFlowAnalysis: 'Cash Flow Analysis',
      WhyThisMatters: 'Why this Matters',
      IncomeTrendCharts: 'Income Trend and Cashflow Charts',
      DebtObligations: 'Debt & Obligations',
      IncomeSource: 'Income Source',
      OpportunitiesRisks: 'Opportunities / Risks',
      RecommendedActions: 'Recommended Actions',
      // Frame A heads both lower tables with these; `Debt & Obligations` carries the third. The
      // last four head the two summary tables, which the frame draws column-headed as well.
      Columns: {
        Item: 'Item',
        Amount: 'Amount',
        Emi: 'EMI',
        Metric: 'Metric',
        Value: 'Value',
        Factor: 'Factor',
        Level: 'Level'
      },
      ViewMore: 'View More'
    }
  }
};
// --- end Customer 360 block copy: Sections 4-6 ---

// --- Customer 360 filter copy: the filter bar (Date Range / Activity Type / Product/Holding) ---
// Category names and pill values are VERBATIM from Frame A (`design/figma-spec.md` § 3.1a): the pill
// draws the SHORT form (`Last 30 days`, `All`, `All`) while the panel row states FR § 9's full
// default, so `PillValues` and `Defaults` are separate strings rather than one reused. Option lists
// and defaults are FR § 9's own.
const customer360FilterCopy = {
  BarLabel: 'Customer 360 filters',
  Categories: {
    dateRange: 'Date Range',
    activityType: 'Activity Type',
    productHolding: 'Product/Holding'
  },
  // `FilterCard` heads each panel with what the category narrows, then its default.
  AppliesTo: {
    dateRange: 'Applies to timeline and activity-based cards',
    activityType: 'Applies to the timeline and cards using the selected activity family',
    productHolding:
      'Applies to holdings, transaction, financial and product-readiness cards where holding-level calculation is supported'
  },
  Defaults: {
    dateRange: 'Last 30 days including today',
    activityType: 'All Activities',
    productHolding: 'All customer holdings'
  },
  PillValues: {
    dateRange: {
      last_7_days: 'Last 7 days',
      last_30_days: 'Last 30 days',
      current_month: 'Current month',
      previous_month: 'Previous month',
      custom: 'Custom range'
    },
    activityType: { all: 'All' },
    productHolding: { all: 'All' }
  },
  Options: {
    dateRange: {
      last_7_days: 'Last 7 days',
      last_30_days: 'Last 30 days',
      current_month: 'Current month',
      previous_month: 'Previous month',
      custom: 'Custom range'
    },
    activityType: {
      all: 'All Activities',
      transactions: 'Transactions',
      campaigns: 'Campaigns',
      rewards_points: 'Rewards & Points',
      reversals: 'Reversals',
      app_microsite_engagement: 'App/Microsite Engagement',
      product_account_activity: 'Product/Account Activity',
      risk_service: 'Risk & Service'
    },
    // The rest of this category is not a fixed list: the holdings come from `C-COM-023` and are
    // named by the payload, so only the default is translated here.
    productHolding: { all: 'All customer holdings' }
  },
  CustomRange: {
    Start: 'Start date',
    End: 'End date',
    StartLabel: 'Custom range start date',
    EndLabel: 'Custom range end date',
    Apply: 'Apply',
    Invalid:
      'Choose a start and end date, with the start on or before the end and neither in the future.'
  }
};
// --- end Customer 360 filter copy ---

const customer360 = {
  PageTitle: 'Customer 360 Intelligence',
  // The page heading the design draws, on both this screen and the customer detail screen, so the
  // two read as one record rather than two pages. PLA-8844.
  PageHeading: 'Customer 360 / {{id}}',
  // The words `PageHeading` opens with, drawn as the link back to the customer listing - this screen
  // has no back button. Its own key rather than a slice of `PageHeading` so a translation can move
  // the separator without the link covering the wrong words; should the two ever disagree the
  // heading simply renders unlinked. PLA-8844.
  PageHeadingLink: 'Customer 360',
  Breadcrumb: {
    Label: 'Breadcrumb',
    Customers: 'Customers'
  },
  Anchor: {
    Active: 'Active',
    Inactive: 'Inactive',
    PerxId: 'Perx ID: {{value}}',
    Identifier: 'Identifier: {{value}}',
    AlternateId: 'Alternate ID: {{value}}'
  },
  // Both states are deliberately generic: FR § 15 keeps every metric payload, customer attribute and
  // upstream error body out of user-facing copy, so neither names what is missing.
  Unavailable: 'Customer intelligence is unavailable for this customer.',
  NoPermittedCards: 'You do not have access to any Customer 360 insights.',
  Cards: customer360Cards,
  Filters: customer360FilterCopy,
  Sections: customer360Sections,
  // The overlay copy every Customer 360 `View More` and `View Details` shares. The funnel overlay's
  // subtitle and its progression line are Layer 1's own `Blocks.FunnelStageSubtitle` /
  // `Blocks.FunnelDroppedOfEntry` rather than a second wording of the same sentence. PLA-8828.
  Breakdown: {
    FunnelTitle: 'Stage Drop-off',
    FunnelJourney: 'Full journey'
  },
  ...customer360Sections456
};

export const translations = {
  [OverwatchKey]: {
    Refresh: {
      Label: 'Refresh data',
      RateLimited: 'Data was refreshed recently. Try again in {{minutes}} min.',
      Failed: 'Could not refresh. Try again later.'
    }
  },
  [CustomerInsightsKey]: {
    PageTitle: 'Customer Insights',
    Actions: actions,
    Blocks: blocks,
    Breakdowns: breakdowns,
    Cards: cards,
    Chart: chart,
    Confidence: confidence,
    ConfidenceGovernance: confidenceGovernance,
    Customer360: customer360,
    DataStates: dataStates,
    DataStateDetails: dataStateDetails,
    DataStateReasons: dataStateReasons,
    Delta: delta,
    Distribution: distribution,
    DynamicTiles: dynamicTiles,
    Filters: filters,
    Freshness: freshness,
    Funnel: funnel,
    Gates: gates,
    Handoff: handoff,
    Header: header,
    ProductReadiness: productReadiness,
    Sections: sections,
    Tooltip: tooltip,
    WhyThisResult: whyThisResult
  }
};
